/**
 * Global Feature Flags for Progressive Optimization
 * Centralized flag system with metadata, KPIs, and rollback commands
 */

import { kpiTracker, type KPIThreshold } from './kpiTracking';
import { isProductionForcedFlag } from './featureFlagsProtection';
import { toast } from 'sonner';
import { isHardcodedFlag, HARDCODED_FLAGS, FLAG_WEEK_MAPPING, PHASE1_WEEKS, PHASE2_WEEKS, PHASE3_WEEKS, PHASE4_CONFIGS, PHASE5_CONFIGS, type WeekConfig } from '@/config/flagsRollout';
import { flagAuditLog } from './featureFlagsAuditLog';

export interface GlobalFeatureFlags {
  // ✅ REMOVED Week 1-2 flags (hardcoded 2025-10-10):
  // - ENABLE_CONSOLIDATED_PERMISSIONS
  // - ENABLE_CURSOR_PAGINATION
  // - ENABLE_SMART_PRELOADING
  // - ENABLE_TIMELINE_VIRTUALIZATION
  // - ENABLE_OPTIMIZED_FILTERS
  
  // ✅ REMOVED Week 3 flags (hardcoded 2025-11-09):
  // - ENABLE_COMBINED_PROJECT_QUERY
  // - ENABLE_PREDICTIVE_TABS
  // - ENABLE_SMART_PREFETCH
  
  // Week 2: Enhanced Monitoring (Production-Forced)
  // ✅ REMOVED 2026-04-30 (PR-C): ENABLE_ENHANCED_MONITORING (zero-consumer)
  
  // ✅ REMOVED Phase 26 flags (hardcoded 2025-11-09):
  // - ENABLE_CUSTOM_MILESTONES
  
  // ✅ NEW: Project Detail Tabs QC & Optimization
  ENABLE_PHOTO_STRIP_VIRTUALIZATION: boolean;
  
  // PHASE 2: SECONDARY FEATURES
  ENABLE_OPTIMIZED_MAINTENANCE_STATE: boolean;
  ENABLE_MAINTENANCE_PERMISSIONS: boolean;
  ENABLE_MAINTENANCE_SMART_PAGINATION: boolean;
  ENABLE_CONSOLIDATED_STAKEHOLDER_PERMISSIONS: boolean;
  ENABLE_OPTIMIZED_PREVIEW_MODE: boolean;
  ENABLE_PREVIEW_COMPONENTS: boolean;
  // ✅ REMOVED: ENABLE_SUBCONS_PERMISSION_CONSOLIDATION (hardcoded 2025-10-10)
  // ✅ REMOVED: ENABLE_OPTIMIZED_STAKEHOLDER_QUERIES (hardcoded 2025-11-09)
  
  // PHASE 3: ADMIN SECTION
  // ✅ REMOVED 2026-04-30 (PR-E): ENABLE_ADMIN_ROLE_CONSOLIDATION (hardcoded true in useAdminRoles)
  ENABLE_BATCH_PERMISSION_UPDATES: boolean;
  ENABLE_PERMISSION_MATRIX_VIRTUALIZATION: boolean;
  ENABLE_ADMIN_PROJECT_CONSOLIDATION: boolean;
  ENABLE_ADMIN_FILTER_OPTIMIZATION: boolean;
  // ✅ RETIRED 2026-05-11 (PR-H1): ENABLE_ENHANCED_ADMIN_MONITORING (orphan)
  // ✅ REMOVED: ENABLE_GLOBAL_PO_TAB (hardcoded, stable since 2025-09)
  // ✅ REMOVED: ENABLE_GLOBAL_BY_DATE_TAB (hardcoded, stable since 2025-09)
  // ✅ REMOVED: ENABLE_GLOBAL_BY_SUBCONS_TAB (hardcoded, stable since 2025-09)
  // ✅ REMOVED: ENABLE_GLOBAL_BY_DATE_FLAT_VIEW (hardcoded, stable since 2025-09)
  
  // PHASE 4A: HIGH PRIORITY PAGES
  ENABLE_USER_MANAGEMENT_VIRTUALIZATION: boolean;
  ENABLE_ADMIN_DASHBOARD_CONSOLIDATION: boolean;
  // ✅ RETIRED 2026-05-11 (PR-H1): ENABLE_ADMIN_STAKEHOLDER_QUERIES (orphan)
  ENABLE_INDEX_PAGE_OPTIMIZATION: boolean;
  
  // PHASE 4B: MEDIUM PRIORITY PAGES
  ENABLE_CATEGORY_CACHING: boolean;
  ENABLE_KEYPLAN_OPTIMIZATION: boolean;
  ENABLE_SCHEDULE_LITE_VIRTUALIZATION: boolean;

  // ✅ REMOVED DIARY FEATURES (hardcoded, stable since 2025-09):
  // - ENABLE_LOCAL_DATE_FILTER (local timezone filtering)
  // - ENABLE_UNIFIED_SUMMARY_LAYOUT (unified summary layout)
  // - ENABLE_SPEC_ITEMS_COLUMN (spec items column)

  // 🪦 RETIRED 2026-05-02 (PR-G3): Procurement Phase 5A flags (5 flags)
  // - ENABLE_PROCUREMENT_REALTIME_V2 (enhanced-path dropped, basic subscription kept)
  // - ENABLE_PROCUREMENT_SMART_PAGINATION (infinite-query path dropped, legacy kept)
  // - ENABLE_PROCUREMENT_ENHANCED_METRICS (zero runtime consumer)
  // - ENABLE_PROCUREMENT_CIRCUIT_BREAKER (breaker logic dropped, fallback unlimited retry kept)
  // - ENABLE_PROCUREMENT_PERMISSION_CACHING (context cache path dropped, individual queries kept)
  
  // COLLECTION MODULE FEATURES
  ENABLE_COLLECTION_MODULE: boolean;
  
  // API OPTIMIZATION PHASE 1
  ENABLE_PAYMENT_MILESTONES_OPTIMIZED_QUERY: boolean;
  ENABLE_CONSOLIDATED_PROCUREMENT_API: boolean;

  // ✅ REMOVED REALTIME flags (hardcoded 2025-11-09):
  // - ENABLE_REALTIME_BACKPRESSURE
  // - ENABLE_BATCH_INVALIDATION
  // - ENABLE_OPTIMIZED_REALTIME_SYNC

  // PHASE 2: I/O & DATABASE OPTIMIZATION
  ENABLE_SELECT_FIELD_TRIMMING: boolean;
  ENABLE_SUBCONS_SMART_PAGINATION: boolean;
  ENABLE_ITEMS_TAB_PAGINATION: boolean;
  
  // ✅ REMOVED 2026-04-30 (PR-B): ENABLE_TIMEZONE_AWARE_FILTERING (hardcoded true, consumer paymentMerger.ts)
  
  // ✅ REMOVED DIARY flags (hardcoded 2025-11-09):
  // - ENABLE_WEEKLY_PLAN_OVERLAP_FILTER
  
  // ✅ REMOVED 2026-04-30 (PR-A): ENABLE_PROXIMITY_CONTEXT_MENU, ENABLE_SPEC_DUPLICATE_BUTTON (zero consumers)

  // ✅ PHASE 6: LEGACY DIARY FLAGS — fully retired
  // ✅ REMOVED 2026-04-30 (PR-B): STRICT_PERMISSION_UI (hardcoded true, consumer useCanManageProjectPermissions.tsx)
  // ✅ REMOVED 2026-04-30 (PR-D): ENABLE_VO_IN_MILESTONE_CALCULATION (hardcoded true in OrderPaymentMilestoneForm + PaymentMilestoneForm) — Phase 6 fully retired
  
  // ✅ BUG FIX: Execute Report PDF Static Milestones (OFF by default)
  ENABLE_EXECUTE_REPORT_STATIC_MILESTONES: boolean;
  
  // ✅ BUG FIX: Execute Report Sort by Start Date (OFF by default)
  ENABLE_EXECUTE_REPORT_SORT_BY_START_DATE: boolean;
  
  // ✅ PHASE 7: PLAN PAGE OPTIMIZATIONS (OFF by default, canary rollout)
  // 🪦 RETIRED 2026-05-02 (PR-G2): 5 Phase 7 Plan flags removed — orphan from day 1, 0 consumers.
  //    ENABLE_PLAN_UNIFIED_DATA, ENABLE_PLAN_ENHANCED_CACHING, ENABLE_PLAN_REALTIME_OPTIMIZATIONS,
  //    ENABLE_PLAN_CONSOLIDATED_STATE, ENABLE_PLAN_TIMELINE_STREAMING. storageVersion bumped v13→v14.
  
  // ✅ PHASE 8: ADMIN SECTION ENHANCEMENTS — fully retired
  // 🪦 RETIRED 2026-05-02 (PR-G1): 7 Admin V2 dead flags — source files deleted upstream, 0 consumers.
  //    Removed: ENABLE_ADMIN_DASHBOARD_V2, ENABLE_USER_MANAGEMENT_ENHANCED, ENABLE_PERMISSION_MATRIX_ADVANCED,
  //             ENABLE_ROLE_MANAGEMENT_V2, ENABLE_PROJECT_CATEGORIES_ENHANCED, ENABLE_STAKEHOLDER_MANAGEMENT_V2,
  //             ENABLE_KEYPLAN_SETUP_ENHANCED. storageVersion bumped v12→v13.
  // 🪦 RETIRED 2026-05-04 (PR-G5): ENABLE_PERFORMANCE_MONITORING, ENABLE_PROCUREMENT_VALIDATION,
  //             ENABLE_RBAC_VALIDATION_LAYER, ENABLE_REALTIME_AUDIT_LOGS, ENABLE_ADVANCED_CACHING,
  //             ENABLE_QUERY_OPTIMIZATION — orphan, 0 consumers. storageVersion bumped v15→v16.
  // 🪦 RETIRED 2026-05-09 (PR-G6): ENABLE_AI_TOOL_CALLING — orphan, 0 functional consumers, AI_TOOL_CALLING_ACCESS permission preserved. storageVersion bumped v16→v17.
  // PR-H2 (2026-05-20): 3 AI Advisor scaffolding flags retired (PROJECT/STAKEHOLDER/PEOPLE_ADVISOR), orphans
  
  // ✅ PHASE 22.1: USER VISIBILITY SIMPLIFICATION (OFF by default)
  ENABLE_SIMPLIFIED_USER_VISIBILITY: boolean;
  
  // ✅ REMOVED 2026-04-30 (PR-A): ENABLE_PRINT_PIN_LAYOUT_FIX (zero consumers, behavior now default)
  
  // ✅ REMOVED SPECS flags (hardcoded 2025-11-09):
  // - ENABLE_SPECS_SYNC_BY_CODE
  // - ENABLE_SPECS_REPORT_LAYOUT_FIX
  
  // ✅ PHASE 23: PIN PHOTO RLS FIX (OFF by default, canary rollout)
  ENABLE_PIN_PHOTO_RLS_V2: boolean;
  
  // ✅ PHASE 24: SPECS ADVANCED FILTERS (OFF by default, canary rollout with auto-rollback)
  ENABLE_SPECS_ADVANCED_FILTERS: boolean;
  
  // ✅ PHASE 1: PERMISSION AND LOGIC ENFORCEMENT (OFF by default, canary rollout)
  ENABLE_AND_PERMISSION_LOGIC: boolean;
  
  // ✅ PHASE 25: SPECS COPY KEYPLAN POSITION (OFF by default, direct public release)
  ENABLE_SPEC_COPY_KEYPLAN_POSITION: boolean;
  
  // ✅ RETIRED 2026-05-01 (PR-F3): ENABLE_PUNCHLIST_LIGHT_V2 (hardcoded true, FORCED_ON_FLAGS entry removed)
  
  // ============================================
  // PHASE 1-5 SCAFFOLDING (legacy)
  // 7 flags retired PR-G4 (May 2026), never wired to runtime:
  //   ENABLE_STRICT_RLS_POLICIES, ENABLE_DATABASE_SECURITY_HARDENING,
  //   ENABLE_QUERY_DEDUPLICATION, ENABLE_STRUCTURED_LOGGING_ONLY,
  //   ENABLE_ZOD_VALIDATION, ENABLE_BUNDLE_SIZE_ENFORCEMENT,
  //   ENABLE_HTTP_CACHING.
  // Remaining: ENABLE_COMPREHENSIVE_ERROR_BOUNDARIES (deferred, real consumers).
  // ============================================
  ENABLE_COMPREHENSIVE_ERROR_BOUNDARIES: boolean;
  
  // ✅ DRAWINGS TAB: Construction drawings management (OFF by default, canary rollout)
  ENABLE_DRAWINGS_TAB: boolean;
  
  // ✅ ROADMAP: Talent Pipeline & Personal Career Dashboard (OFF by default, canary rollout)
  ENABLE_ROADMAP: boolean;
  
  // ✅ RETIRED 2026-05-11 (PR-H1): ENABLE_CRM + ENABLE_CRM_MOCKUP_DATA (orphans;
  // PR-E previously retired sibling ENABLE_STAKEHOLDER_CRM_DASHBOARD)
  
  // ✅ REMOVED 2026-04-30 (PR-E): ENABLE_STAKEHOLDER_CRM_DASHBOARD (hardcoded true; dashboard always rendered)
  
  // ✅ SAFE NAVIGATION: Auto-reload control (OFF by default)
  ENABLE_SAFE_NAVIGATION_AUTO_RELOAD: boolean;
  
  // ✅ PROGRESSIVE LAYER LOADING: Execute tab optimization (OFF by default, canary rollout)
  ENABLE_PROGRESSIVE_LAYER_LOADING: boolean;
  
  // ✅ REMOVED Phase 29 flags (hardcoded 2025-11-09):
  // - ENABLE_OPTIMIZED_LAYER_IMAGES
  
  // ✅ PHASE 1: DRAWING FILES OPTIMIZATION (OFF by default, canary rollout)
  ENABLE_OPTIMIZED_DRAWING_UPLOADS: boolean;
  
  // ✅ DRAWING PDF QUALITY SETTINGS: Configurable PDF export resolution (OFF by default, Light QA Contract compliant)
  ENABLE_DRAWING_PDF_QUALITY_SETTINGS: boolean;
  
  // ✅ SPECS REPORT V2: 3-Layer Architecture with Direct PDF Export (OFF by default, canary rollout)
  ENABLE_SPECS_REPORT_V2: boolean;
  
  // ✅ API OPTIMIZATION: Progressive API improvements (OFF by default, Light QA Contract compliant)
  ENABLE_API_QUERY_KEY_CONSOLIDATION: boolean;
  ENABLE_API_OPTIMISTIC_UPDATES: boolean;
  // ✅ RETIRED 2026-05-11 (PR-H1): ENABLE_API_TYPE_EXPORTS (orphan)
  
  // ✅ ROADMAP DRAFT PERSISTENCE: Prevent data loss in Manage Categories dialog (OFF by default, Light QA Contract compliant)
  ENABLE_ROADMAP_CATEGORIES_DRAFT_PERSISTENCE: boolean;
  
  // ✅ ITEM TYPE FILTERING: Distinguish build vs service items (OFF by default, canary rollout)
  ENABLE_ITEM_TYPE_FILTERING: boolean;
  
  // ✅ RETIRED 2026-05-01 (PR-F3): ENABLE_TIMELINE_REPORT_ASSESSMENT (hardcoded true in consumers, forcedFlags entry removed)
  
  // ✅ SIMPLE PERMISSION MANAGER: Simplified permission management with real-time sync (OFF by default, canary rollout)
  ENABLE_SIMPLE_PERMISSION_MANAGER: boolean;
  
  // ✅ GDRIVE PROJECT NAME FOLDERS: Use project names instead of project-{id} for folders (OFF by default, Light QA Contract compliant)
  ENABLE_GDRIVE_PROJECT_NAME_FOLDERS: boolean;
  
  // ✅ RETIRED 2026-05-01 (PR-F2.5a): ENABLE_NEW_GDRIVE_FOLDERS (hardcoded true in consumers, backend env var preserved)
  
  // ✅ GDRIVE PERMISSION SYNC: Automatic Google Drive permission synchronization (OFF by default, Light QA Contract compliant)
  ENABLE_GDRIVE_PERMISSION_SYNC: boolean;
  
   // ✅ RETIRED 2026-05-01 (PR-F2): ENABLE_NEW_GDRIVE_FOLDER_PERMISSIONS (hardcoded true, consolidatedPerms VIEW_FOLDER_* preserved)
  
  // ✅ P&L PERMISSIONS: Use PL_ACCESS permission instead of Collection+Procurement combo
  ENABLE_PL_PERMISSIONS: boolean;
  
  // ✅ RETIRED 2026-04-30 (PR-F1): ENABLE_SITE_MODULE (hardcoded true in consumers, permission-gated by SITE_ACCESS)
  
  // ✅ RETIRED 2026-05-01 (PR-F3): ENABLE_AI_PLAN_ADVISOR (hardcoded true in consumers, protection entry removed)
  
  // ✅ SITE PHOTO STATUS: Replace category tabs with status-based photo management (OFF by default, Light QA Contract compliant)
  ENABLE_SITE_PHOTO_STATUS: boolean;
  
  // ✅ RETIRED 2026-04-30 (PR-F1): ENABLE_SERVICE_ITEM_ASSIGNMENT (hardcoded true, isServiceProject guard preserved)
  
  // ✅ SITE LAZY LOAD SECTIONS: Lazy load collapsed sections in Site page (OFF by default, Light QA Contract compliant)
  ENABLE_SITE_LAZY_LOAD_SECTIONS: boolean;
  
  // ✅ RETIRED 2026-05-11 (PR-H1): ENABLE_ROADMAP_DELETE_UI (orphan)
  
  // ✅ FORCE REFRESH: Force all users to refresh browser for latest version (OFF by default, Light QA Contract compliant)
  ENABLE_FORCE_REFRESH: boolean;
  
  // ✅ HR MODULE: Full HR Management System with 6-tab employee profile (OFF by default, Light QA Contract compliant)
  ENABLE_HR_MODULE: boolean;
  
  // ✅ PROJECT ROLES: Enable project-specific roles (SOL/PM/CD/QS/SM) in Resource tab (OFF by default, Light QA Contract compliant)
  ENABLE_PROJECT_ROLES: boolean;
  
  // ✅ PEOPLE MODULE: Unified People Module merging Roadmap + HR (OFF by default, gradual rollout)
  ENABLE_PEOPLE_MODULE: boolean;
  
   // ✅ RETIRED 2026-05-01 (PR-F2): ENABLE_PERMIT_TAB (hardcoded true, PERMIT_VIEW/CREATE/EDIT/DELETE preserved)
   
   // ✅ RETIRED 2026-05-01 (PR-F2): ENABLE_PERMIT_SHARE (hardcoded true, PERMIT_SHARE preserved)
   
   // ✅ RETIRED 2026-05-01 (PR-F2): ENABLE_MEETING_MODULE (hardcoded true, hasMeetingAccess preserved)
  
  // ✅ AI TOOL CALLING: REMOVED - feature discontinued
  
  // 🪦 RETIRED 2026-05-20 (PR-H2): ENABLE_AI_PROJECT_ADVISOR — orphan, 0 consumers, useAdminCheck gates page independently
  // 🪦 RETIRED 2026-05-20 (PR-H2): ENABLE_AI_STAKEHOLDER_ADVISOR — orphan, 0 consumers, useAdminCheck gates page independently
  // 🪦 RETIRED 2026-05-20 (PR-H2): ENABLE_AI_PEOPLE_ADVISOR — orphan, 0 consumers, useAdminCheck gates page independently
  
  // ✅ RETIRED 2026-04-30 (PR-F1): ENABLE_NEEDS_TAB (hardcoded true, canAccessNeeds permission preserved)
  // ✅ RETIRED 2026-04-30 (PR-F1): ENABLE_QAQC_TAB (hardcoded true, canAccessQAQC permission preserved)
  
  // ✅ CLOSING TAB: Project closing summary with evaluation and key learnings (OFF by default, Light QA Contract compliant)
  ENABLE_CLOSING_TAB: boolean;
  
  // ✅ CONTROL CENTER Phase 1: Activity Log (OFF by default, Light QA Contract compliant)
  ENABLE_ACTIVITY_LOG: boolean;
  
  // ✅ CONTROL CENTER Phase 2: Sign-off System (OFF by default, Light QA Contract compliant)
  ENABLE_SIGN_OFF_SYSTEM: boolean;
  
  // ✅ SITE FORMS: Dynamic forms/requests system in Site module (OFF by default, Light QA Contract compliant)
  ENABLE_SITE_FORMS: boolean;
}

export interface CanaryConfig {
  enabled: boolean;           // Canary mode ON/OFF
  percentage: number;         // % traffic (0-100)
  gates: {                    // Auto-rollback thresholds
    errorRate: number;        // Max error rate (e.g., 0.01 = 1%)
    p95Latency: number;       // Max P95 latency (ms)
    cacheHitRate: number;     // Min cache hit rate (e.g., 0.80 = 80%)
  };
  startedAt?: number;         // Timestamp when canary started
  userId?: string[];          // Optional: whitelist user IDs for testing
}

export interface FlagMetadata {
  enabled: boolean;
  owner: string;
  expiryDate?: string;
  rollbackCommand: string;
  kpis: KPIThreshold[];
  description?: string;
  phase?: string;
  enabledAt?: number;
  performanceBudget?: {
    loadingTimeReduction: string;
    dbCallReduction: string;
    memoryIncrease: string;
  };
  rollbackCriteria?: string[];
  hardcoded?: boolean;
  hardcodedDate?: string;
  hardcodedReason?: string;
  canary?: CanaryConfig;      // ✅ NEW: Canary rollout configuration
}

class GlobalFeatureFlagManager {
  private flags: Record<keyof GlobalFeatureFlags, FlagMetadata> = {
    // ✅ REMOVED: Week 1-2 flags (hardcoded 2025-10-10)
    // ✅ REMOVED 2026-04-30 (PR-C): ENABLE_ENHANCED_MONITORING metadata (zero-consumer)
    // ✅ REMOVED: ENABLE_CUSTOM_MILESTONES (hardcoded 2025-11-09)
    // ✅ REMOVED: ENABLE_COMBINED_PROJECT_QUERY (hardcoded 2025-11-09)
    // ✅ REMOVED: ENABLE_PREDICTIVE_TABS (hardcoded 2025-11-09)
    
    // ✅ NEW: Project Detail Tabs Optimization (Phase 1 - Critical Fixes)
    ENABLE_PHOTO_STRIP_VIRTUALIZATION: {
      enabled: false, // ✅ Temporarily disabled to fix lazy loading conflicts with nested scroll
      owner: 'diary-team',
      expiryDate: '2025-12-31',
      rollbackCommand: 'globalFeatureFlags.disable("ENABLE_PHOTO_STRIP_VIRTUALIZATION")',
      kpis: ['Execute LCP: 4s → 1.5s (-66%)', 'Memory: -10MB', 'FPS: 60+'],
      description: 'Virtualizes photo strip rendering in Execute tab for better performance',
      phase: 'phase-1-critical',
      performanceBudget: {
        loadingTimeReduction: '60%',
        dbCallReduction: '0%',
        memoryIncrease: '5%'
      },
      rollbackCriteria: ['LCP > 3s', 'Memory leak detected', 'Scroll FPS < 30']
    },
    
    // ✅ REMOVED: ENABLE_SMART_PREFETCH (hardcoded 2025-11-09)
    ENABLE_OPTIMIZED_MAINTENANCE_STATE: {
      enabled: false,
      owner: 'platform-team',
      rollbackCommand: 'globalFeatureFlags.disable("ENABLE_OPTIMIZED_MAINTENANCE_STATE")',
      kpis: [],
      description: 'Optimizes the maintenance state management for better performance.',
      phase: 'Phase 2',
      performanceBudget: {
        loadingTimeReduction: '10%',
        dbCallReduction: '15%',
        memoryIncrease: '3%'
      },
      rollbackCriteria: ['Maintenance state errors', 'Slow state updates']
    },
    ENABLE_MAINTENANCE_PERMISSIONS: {
      enabled: false,
      owner: 'platform-team',
      rollbackCommand: 'globalFeatureFlags.disable("ENABLE_MAINTENANCE_PERMISSIONS")',
      kpis: [],
      description: 'Enables consolidated permission checks for maintenance tasks.',
      phase: 'Phase 2',
      performanceBudget: {
        loadingTimeReduction: '8%',
        dbCallReduction: '12%',
        memoryIncrease: '0%'
      },
      rollbackCriteria: ['Permission errors', 'Unauthorized access']
    },
    ENABLE_MAINTENANCE_SMART_PAGINATION: {
      enabled: false,
      owner: 'platform-team',
      rollbackCommand: 'globalFeatureFlags.disable("ENABLE_MAINTENANCE_SMART_PAGINATION")',
      kpis: [],
      description: 'Implements smart pagination for maintenance logs.',
      phase: 'Phase 2',
      performanceBudget: {
        loadingTimeReduction: '12%',
        dbCallReduction: '18%',
        memoryIncrease: '5%'
      },
      rollbackCriteria: ['Pagination errors', 'Incomplete log data']
    },
    ENABLE_CONSOLIDATED_STAKEHOLDER_PERMISSIONS: {
      enabled: false,
      owner: 'platform-team',
      rollbackCommand: 'globalFeatureFlags.disable("ENABLE_CONSOLIDATED_STAKEHOLDER_PERMISSIONS")',
      kpis: [],
      description: 'Consolidates stakeholder permission checks.',
      phase: 'Phase 2',
      performanceBudget: {
        loadingTimeReduction: '10%',
        dbCallReduction: '15%',
        memoryIncrease: '0%'
      },
      rollbackCriteria: ['Permission errors', 'Unauthorized access']
    },
    // ✅ REMOVED: ENABLE_OPTIMIZED_STAKEHOLDER_QUERIES (hardcoded 2025-11-09)
    ENABLE_OPTIMIZED_PREVIEW_MODE: {
      enabled: false,
      owner: 'platform-team',
      rollbackCommand: 'globalFeatureFlags.disable("ENABLE_OPTIMIZED_PREVIEW_MODE")',
      kpis: [],
      description: 'Optimizes the preview mode for faster loading.',
      phase: 'Phase 2',
      performanceBudget: {
        loadingTimeReduction: '18%',
        dbCallReduction: '25%',
        memoryIncrease: '2%'
      },
      rollbackCriteria: ['Preview rendering issues', 'Slow loading times']
    },
    ENABLE_PREVIEW_COMPONENTS: {
      enabled: false,
      owner: 'platform-team',
      rollbackCommand: 'globalFeatureFlags.disable("ENABLE_PREVIEW_COMPONENTS")',
      kpis: [],
      description: 'Enables optimized components in preview mode.',
      phase: 'Phase 2',
      performanceBudget: {
        loadingTimeReduction: '12%',
        dbCallReduction: '18%',
        memoryIncrease: '0%'
      },
      rollbackCriteria: ['Component rendering issues', 'Incompatible components']
    },
    // ✅ REMOVED: ENABLE_SUBCONS_PERMISSION_CONSOLIDATION (hardcoded 2025-10-10)
    // ✅ REMOVED 2026-04-30 (PR-E): ENABLE_ADMIN_ROLE_CONSOLIDATION metadata block (hardcoded in useAdminRoles)
    ENABLE_BATCH_PERMISSION_UPDATES: {
      enabled: false,
      owner: 'platform-team',
      rollbackCommand: 'globalFeatureFlags.disable("ENABLE_BATCH_PERMISSION_UPDATES")',
      kpis: [],
      description: 'Enables batch updates for permissions.',
      phase: 'Phase 3',
      performanceBudget: {
        loadingTimeReduction: '15%',
        dbCallReduction: '20%',
        memoryIncrease: '5%'
      },
      rollbackCriteria: ['Update errors', 'Inconsistent permissions']
    },
    ENABLE_PERMISSION_MATRIX_VIRTUALIZATION: {
      enabled: false,
      owner: 'platform-team',
      rollbackCommand: 'globalFeatureFlags.disable("ENABLE_PERMISSION_MATRIX_VIRTUALIZATION")',
      kpis: [],
      description: 'Virtualizes the permission matrix for better performance.',
      phase: 'Phase 3',
      performanceBudget: {
        loadingTimeReduction: '18%',
        dbCallReduction: '25%',
        memoryIncrease: '8%'
      },
      rollbackCriteria: ['Rendering issues', 'Missing permissions']
    },
    ENABLE_ADMIN_PROJECT_CONSOLIDATION: {
      enabled: false,
      owner: 'platform-team',
      rollbackCommand: 'globalFeatureFlags.disable("ENABLE_ADMIN_PROJECT_CONSOLIDATION")',
      kpis: [],
      description: 'Consolidates admin project queries.',
      phase: 'Phase 3',
      performanceBudget: {
        loadingTimeReduction: '12%',
        dbCallReduction: '18%',
        memoryIncrease: '3%'
      },
      rollbackCriteria: ['Query errors', 'Incomplete project data']
    },
    ENABLE_ADMIN_FILTER_OPTIMIZATION: {
      enabled: false,
      owner: 'platform-team',
      rollbackCommand: 'globalFeatureFlags.disable("ENABLE_ADMIN_FILTER_OPTIMIZATION")',
      kpis: [],
      description: 'Optimizes admin filter logic.',
      phase: 'Phase 3',
      performanceBudget: {
        loadingTimeReduction: '10%',
        dbCallReduction: '15%',
        memoryIncrease: '0%'
      },
      rollbackCriteria: ['Filter errors', 'Slow filter application']
    },
    // ✅ RETIRED 2026-05-11 (PR-H1): ENABLE_ENHANCED_ADMIN_MONITORING metadata (orphan)
    // ✅ REMOVED: Global tab flags (hardcoded since 2025-09)
    // - ENABLE_GLOBAL_PO_TAB
    // - ENABLE_GLOBAL_BY_DATE_TAB
    // - ENABLE_GLOBAL_BY_SUBCONS_TAB
    // - ENABLE_GLOBAL_BY_DATE_FLAT_VIEW
    ENABLE_USER_MANAGEMENT_VIRTUALIZATION: {
      enabled: false,
      owner: 'platform-team',
      rollbackCommand: 'globalFeatureFlags.disable("ENABLE_USER_MANAGEMENT_VIRTUALIZATION")',
      kpis: [],
      description: 'Virtualizes user management for better performance.',
      phase: 'Phase 4A',
      performanceBudget: {
        loadingTimeReduction: '18%',
        dbCallReduction: '25%',
        memoryIncrease: '8%'
      },
      rollbackCriteria: ['Rendering issues', 'Missing users']
    },
    ENABLE_ADMIN_DASHBOARD_CONSOLIDATION: {
      enabled: false,
      owner: 'platform-team',
      rollbackCommand: 'globalFeatureFlags.disable("ENABLE_ADMIN_DASHBOARD_CONSOLIDATION")',
      kpis: [],
      description: 'Consolidates admin dashboard queries.',
      phase: 'Phase 4A',
      performanceBudget: {
        loadingTimeReduction: '12%',
        dbCallReduction: '18%',
        memoryIncrease: '3%'
      },
      rollbackCriteria: ['Query errors', 'Incomplete data']
    },
    // ✅ RETIRED 2026-05-11 (PR-H1): ENABLE_ADMIN_STAKEHOLDER_QUERIES metadata (orphan)
    ENABLE_INDEX_PAGE_OPTIMIZATION: {
      enabled: false,
      owner: 'platform-team',
      rollbackCommand: 'globalFeatureFlags.disable("ENABLE_INDEX_PAGE_OPTIMIZATION")',
      kpis: [],
      description: 'Optimizes the index page for faster loading.',
      phase: 'Phase 4A',
      performanceBudget: {
        loadingTimeReduction: '15%',
        dbCallReduction: '20%',
        memoryIncrease: '5%'
      },
      rollbackCriteria: ['Loading issues', 'Incomplete data']
    },
    ENABLE_CATEGORY_CACHING: {
      enabled: false,
      owner: 'platform-team',
      rollbackCommand: 'globalFeatureFlags.disable("ENABLE_CATEGORY_CACHING")',
      kpis: [],
      description: 'Caches category data for faster retrieval.',
      phase: 'Phase 4B',
      performanceBudget: {
        loadingTimeReduction: '8%',
        dbCallReduction: '12%',
        memoryIncrease: '2%'
      },
      rollbackCriteria: ['Cache errors', 'Inconsistent data']
    },
    ENABLE_KEYPLAN_OPTIMIZATION: {
      enabled: false,
      owner: 'platform-team',
      rollbackCommand: 'globalFeatureFlags.disable("ENABLE_KEYPLAN_OPTIMIZATION")',
      kpis: [],
      description: 'Optimizes keyplan-related operations.',
      phase: 'Phase 4B',
      performanceBudget: {
        loadingTimeReduction: '10%',
        dbCallReduction: '15%',
        memoryIncrease: '0%'
      },
      rollbackCriteria: ['Operation errors', 'Slow performance']
    },
    ENABLE_SCHEDULE_LITE_VIRTUALIZATION: {
      enabled: false,
      owner: 'platform-team',
      rollbackCommand: 'globalFeatureFlags.disable("ENABLE_SCHEDULE_LITE_VIRTUALIZATION")',
      kpis: [],
      description: 'Virtualizes the schedule lite component.',
      phase: 'Phase 4B',
      performanceBudget: {
        loadingTimeReduction: '12%',
        dbCallReduction: '18%',
        memoryIncrease: '3%'
      },
      rollbackCriteria: ['Rendering issues', 'Missing data']
    },
    // ✅ REMOVED: Diary flags (hardcoded since 2025-09)
    // - ENABLE_LOCAL_DATE_FILTER
    // - ENABLE_UNIFIED_SUMMARY_LAYOUT
    // - ENABLE_SPEC_ITEMS_COLUMN
    // 🪦 RETIRED 2026-05-02 (PR-G3): 5 Procurement Phase 5A metadata blocks removed
    
    // COLLECTION MODULE
    ENABLE_COLLECTION_MODULE: {
      enabled: true,
      owner: 'collection-team',
      rollbackCommand: 'globalFeatureFlags.disable("ENABLE_COLLECTION_MODULE")',
      kpis: [
        { metric: 'receipt_creation_time', threshold: 2000, operator: '<' as const },
        { metric: 'expense_calculation_accuracy', threshold: 100, operator: '=' as const }
      ],
      description: 'Collection Management module với real-time expense tracking, calendar view, và CRUD operations',
      phase: 'Collection V1',
      performanceBudget: {
        loadingTimeReduction: 'N/A (new feature)',
        dbCallReduction: 'N/A (new feature)',
        memoryIncrease: '<10%'
      },
      rollbackCriteria: [
        'Receipt creation errors >5%',
        'Expense calculation discrepancies',
        'Permission check failures',
        'Calendar view performance issues'
      ]
    },
    // ✅ REMOVED: ENABLE_REALTIME_BACKPRESSURE (hardcoded 2025-11-09)
    // ✅ REMOVED: ENABLE_BATCH_INVALIDATION (hardcoded 2025-11-09)
    // ✅ REMOVED: ENABLE_OPTIMIZED_REALTIME_SYNC (hardcoded 2025-11-09)
    ENABLE_SELECT_FIELD_TRIMMING: {
      enabled: false,
      owner: 'platform-team',
      rollbackCommand: 'globalFeatureFlags.disable("ENABLE_SELECT_FIELD_TRIMMING")',
      kpis: [],
      description: 'Replaces SELECT * with specific column lists to reduce payload size',
      phase: 'Phase 2: I/O & Database',
      performanceBudget: {
        loadingTimeReduction: '5-10%',
        dbCallReduction: '0%',
        memoryIncrease: '0%'
      },
      rollbackCriteria: ['Missing data fields', 'UI rendering errors']
    },
    ENABLE_SUBCONS_SMART_PAGINATION: {
      enabled: false, // ✅ OFF by default
      owner: 'subcons-team',
      expiryDate: '2025-12-31',
      rollbackCommand: 'globalFeatureFlags.disable("ENABLE_SUBCONS_SMART_PAGINATION")',
      kpis: [
        { metric: 'initial_load_time', threshold: 500, operator: '<' as const },
        { metric: 'network_payload_reduction', threshold: 80, operator: '>=' as const },
        { metric: 'error_rate', threshold: 1, operator: '<' as const },
        { metric: 'cache_hit_rate', threshold: 85, operator: '>=' as const }
      ],
      description: 'Keyset pagination cho tab Subcons (20 rows/page) với pg_trgm index',
      phase: 'phase-2-io-optimization',
      performanceBudget: {
        loadingTimeReduction: '60%',
        dbCallReduction: '0%', // Same query, just paginated
        memoryIncrease: '0%'
      },
      rollbackCriteria: [
        'Load time > 800ms',
        'Pagination errors',
        'Missing subcontractors in view',
        'Search accuracy < 95%'
      ]
    },
    ENABLE_ITEMS_TAB_PAGINATION: {
      enabled: false,
      owner: 'subcons-team',
      expiryDate: '2025-12-31',
      rollbackCommand: 'globalFeatureFlags.disable("ENABLE_ITEMS_TAB_PAGINATION")',
      kpis: [
        { metric: 'initial_load_time', threshold: 600, operator: '<' as const },
        { metric: 'network_payload_reduction', threshold: 70, operator: '>=' as const },
        { metric: 'error_rate', threshold: 1, operator: '<' as const },
        { metric: 'join_query_time', threshold: 400, operator: '<' as const }
      ],
      description: 'Pagination cho tab Items (30 categories/page) với JOIN query',
      phase: 'phase-2-io-optimization',
      performanceBudget: {
        loadingTimeReduction: '50%',
        dbCallReduction: '70%',
        memoryIncrease: '0%'
      },
      rollbackCriteria: [
        'Load time > 1s',
        'Category data incomplete',
        'Subcontractor mapping errors',
        'JOIN query timeout'
      ]
    },
    // ✅ REMOVED 2026-04-30 (PR-B): ENABLE_TIMEZONE_AWARE_FILTERING metadata (hardcoded true in paymentMerger.ts)

    // ✅ REMOVED: ENABLE_WEEKLY_PLAN_OVERLAP_FILTER (hardcoded 2025-11-09)
    // ✅ REMOVED 2026-04-30 (PR-A): ENABLE_PROXIMITY_CONTEXT_MENU, ENABLE_SPEC_DUPLICATE_BUTTON

    // ========================================
    // PHASE 6: Legacy Diary Flags — fully retired
    // ========================================
    // ✅ REMOVED 2026-04-30 (PR-B): STRICT_PERMISSION_UI metadata (hardcoded true in useCanManageProjectPermissions.tsx)
    // ✅ REMOVED 2026-04-30 (PR-D): ENABLE_VO_IN_MILESTONE_CALCULATION metadata (hardcoded true in consumers) — Phase 6 fully retired

    // ========================================
    // PHASE 7: Plan Page Optimizations — fully retired
    // ========================================
    // 🪦 RETIRED 2026-05-02 (PR-G2): 5 Phase 7 metadata blocks removed (orphan from day 1, 0 consumers).

    // ========================================
    // PHASE 8: Admin Section Enhancements — fully retired
    // ========================================
    // 🪦 RETIRED 2026-05-02 (PR-G1): 7 Admin V2 metadata blocks removed — flags retired, 0 consumers.
    // 🪦 RETIRED 2026-05-04 (PR-G5): 6 remaining Phase 8 metadata blocks removed — orphan, 0 consumers.
    //    PERFORMANCE_MONITORING, PROCUREMENT_VALIDATION, RBAC_VALIDATION_LAYER,
    //    REALTIME_AUDIT_LOGS, ADVANCED_CACHING, QUERY_OPTIMIZATION.

    // ========================================
    // PHASE 22.1: User Visibility Simplification
    // ========================================
    ENABLE_SIMPLIFIED_USER_VISIBILITY: {
      enabled: false, // ✅ Start with canary rollout
      owner: 'platform-team',
      expiryDate: '2025-12-31',
      rollbackCommand: 'globalFeatureFlags.disable("ENABLE_SIMPLIFIED_USER_VISIBILITY")',
      kpis: [
        { metric: 'profile_query_latency_p95', threshold: 500, operator: '<' as const },
        { metric: 'cache_hit_rate', threshold: 85, operator: '>=' as const },
        { metric: 'hidden_users_count', threshold: 50, operator: '<' as const }
      ],
      description: 'Company + Project auto-visibility with exception-based hiding',
      phase: 'Phase 22.1',
      performanceBudget: {
        loadingTimeReduction: '95%', // 480 rows → ~5 rows query
        dbCallReduction: '0%',
        memoryIncrease: '0%'
      },
      rollbackCriteria: [
        'Profile visibility broken (users cannot see expected profiles)',
        'Hidden users table > 50 rows (indicates misconfiguration)',
        'Query latency > 500ms',
        'Cache hit rate < 80%'
      ]
    },
    
    // ✅ REMOVED 2026-04-30 (PR-A): ENABLE_PRINT_PIN_LAYOUT_FIX (zero consumers)
    
    // ✅ REMOVED: ENABLE_SPECS_SYNC_BY_CODE (hardcoded 2025-11-09)
    
    // ========================================
    // PHASE 23: PIN PHOTO RLS FIX
    // ========================================
    ENABLE_PIN_PHOTO_RLS_V2: {
      enabled: false, // ✅ OFF - requires manual DB migration
      owner: 'diary-team',
      expiryDate: '2025-12-31',
      rollbackCommand: 'globalFeatureFlags.disable("ENABLE_PIN_PHOTO_RLS_V2")',
      kpis: [
        { metric: 'pin_upload_success_rate', threshold: 100, operator: '==' as const },
        { metric: 'storage_403_errors', threshold: 0, operator: '==' as const },
        { metric: 'upload_latency_p95', threshold: 500, operator: '<' as const }
      ],
      description: 'V2 RLS policies for diary-photos bucket - supports pins/* paths without project_id',
      phase: 'Phase 23 - Pin Photo Upload Fix',
      performanceBudget: {
        loadingTimeReduction: '0%',
        dbCallReduction: '0%',
        memoryIncrease: '0%'
      },
      rollbackCriteria: [
        'Pin upload failures (403 errors)',
        'Project photo upload broken',
        'Permission bypass detected'
      ],
      canary: {
        enabled: true,
        percentage: 10,
        gates: {
          errorRate: 0.01,
          p95Latency: 500,
          cacheHitRate: 0
        }
      }
    },
    
    // ========================================
    // PHASE 24: SPECS ADVANCED FILTERS
    // ========================================
    ENABLE_SPECS_ADVANCED_FILTERS: {
      enabled: true,
      owner: 'diary-team',
      expiryDate: '2025-12-31',
      rollbackCommand: 'globalFeatureFlags.disable("ENABLE_SPECS_ADVANCED_FILTERS")',
      kpis: [
        { metric: 'filter_computation_time', threshold: 100, operator: '<' as const },
        { metric: 'pin_sync_latency', threshold: 50, operator: '<' as const },
        { metric: 'filter_error_rate', threshold: 0.01, operator: '<' as const }
      ],
      description: 'Multi-select Code and Supplier filters with pin filtering sync',
      phase: 'Phase 24 - Specs Advanced Filters',
      performanceBudget: {
        loadingTimeReduction: '0%',
        dbCallReduction: '0%',
        memoryIncrease: '2%'
      },
      rollbackCriteria: [
        'Filter computation >100ms',
        'Pin sync broken',
        'Error rate >1%'
      ],
      canary: {
        enabled: true, // ✅ PHASE QA: Re-enable canary for 5% rollout with auto-rollback
        percentage: 5, // Start with 5% of users
        gates: {
          errorRate: 0.01,    // Max 1% error rate
          p95Latency: 200,    // Max 200ms filter render time (p95)
          cacheHitRate: 0.80  // Min 80% cache hit rate
        },
        startedAt: Date.now() // Track when canary started
      }
    },
    
    // ✅ REMOVED: ENABLE_SPECS_REPORT_LAYOUT_FIX (hardcoded 2025-11-09)
    
    // ========================================
    // ✅ PHASE 1: PERMISSION AND LOGIC ENFORCEMENT
    // ========================================
    ENABLE_AND_PERMISSION_LOGIC: {
      enabled: false, // ✅ OFF by default - Canary rollout
      owner: 'platform-team',
      expiryDate: '2025-12-31',
      rollbackCommand: 'globalFeatureFlags.disable("ENABLE_AND_PERMISSION_LOGIC")',
      kpis: [
        { metric: 'privilege_escalation_count', threshold: 0, operator: '==' as const },
        { metric: 'permission_check_p95', threshold: 50, operator: '<' as const },
        { metric: 'rls_block_rate', threshold: 95, operator: '>' as const },
        { metric: 'permission_error_rate', threshold: 1, operator: '<' as const }
      ],
      description: 'Enforce AND logic between Global and Project-level permissions. Users need BOTH global permission AND project permission to perform actions.',
      phase: 'permission-and-logic-v1',
      performanceBudget: {
        loadingTimeReduction: '0%',
        dbCallReduction: '0%',
        memoryIncrease: '0%'
      },
      rollbackCriteria: [
        'Users cannot access valid resources',
        'Permission check errors > 1%',
        'Performance degradation > 10%',
        'RLS policy failures'
      ],
      canary: {
        enabled: false, // ✅ Start with full rollout disabled
        percentage: 0,   // 0% → 5% → 50% → 100%
        gates: {
          errorRate: 0.01,      // Max 1% error
          p95Latency: 200,      // Max 200ms
          cacheHitRate: 0.85    // Min 85% cache hit
        }
      }
    },
    
    // ========================================
    // PHASE 25: SPECS COPY KEYPLAN POSITION
    // ========================================
    ENABLE_SPEC_COPY_KEYPLAN_POSITION: {
      enabled: false, // ✅ OFF by default, direct public release (no canary)
      owner: 'specs-team',
      expiryDate: '2025-12-31',
      rollbackCommand: 'globalFeatureFlags.disable("ENABLE_SPEC_COPY_KEYPLAN_POSITION")',
      kpis: [
        { metric: 'spec_copy_duration', threshold: 2000, operator: '<' as const },
        { metric: 'spec_copy_error_rate', threshold: 1, operator: '<' as const },
        { metric: 'keyplan_copy_success_rate', threshold: 95, operator: '>' as const }
      ],
      description: 'Copy spec items to same keyplan position (specs_layer_id or layer_id)',
      phase: 'phase-25-specs-copy',
      performanceBudget: {
        loadingTimeReduction: '0%', // No page load impact
        dbCallReduction: '0%',
        memoryIncrease: '0%'
      },
      rollbackCriteria: [
        'Copy duration > 5s',
        'Error rate > 1%',
        'User reports incorrect position',
        'RLS violations detected'
      ]
    },
    
    // ========================================
    // DRAWINGS TAB: Construction drawings management
    // ========================================
    ENABLE_DRAWINGS_TAB: {
      enabled: true, // ✅ Enabled - permissions added
      owner: 'construction-team',
      expiryDate: '2025-12-31',
      rollbackCommand: 'globalFeatureFlags.disable("ENABLE_DRAWINGS_TAB")',
      kpis: [
        { metric: 'lcp_drawings_tab', threshold: 2500, operator: '<' as const },
        { metric: 'drawings_error_rate', threshold: 1, operator: '<' as const },
        { metric: 'bundle_size_drawings', threshold: 500, operator: '<' as const }
      ],
      description: 'Construction drawings management with layers, zones, and workflow tracking',
      phase: 'drawings-v1',
      performanceBudget: {
        loadingTimeReduction: 'LCP < 2.5s',
        dbCallReduction: '< 10 queries per page',
        memoryIncrease: '< 50MB',
      },
      rollbackCriteria: [
        'LCP > 3s for 5 minutes',
        'Error rate > 1%',
        'Bundle size > 500KB gzipped',
        'User reports: Cannot view drawings',
      ],
      canary: {
        enabled: false,
        percentage: 0,
        gates: {
          errorRate: 0.01, // 1%
          p95Latency: 2500, // 2.5s
          cacheHitRate: 0.80, // 80%
        },
      },
    },
    
    // ========================================
    // ROADMAP: Talent Pipeline & Personal Dashboard
    // ========================================
    ENABLE_ROADMAP: {
      enabled: true, // ✅ Enabled with granular permissions (ROADMAP_VIEW_ALL, ROADMAP_EDIT, ROADMAP_EXPORT, ROADMAP_DELETE)
      owner: 'hr-team',
      expiryDate: '2025-12-31',
      rollbackCommand: 'globalFeatureFlags.disable("ENABLE_ROADMAP")',
      audit: {
        enabledAt: '2025-11-04',
        enabledBy: 'system',
        reason: 'Rollout granular permissions refactoring per Light QA Contract'
      },
      kpis: [
        { metric: 'lcp_roadmap', threshold: 2000, operator: '<' as const },
        { metric: 'timeline_render', threshold: 500, operator: '<' as const },
        { metric: 'roadmap_error_rate', threshold: 1, operator: '<' as const }
      ],
      description: 'Talent Pipeline (Manager Dashboard) & Personal Career Dashboard for employee development tracking',
      phase: 'Phase 28 - Roadmap',
      performanceBudget: {
        loadingTimeReduction: '0%',
        dbCallReduction: '0%',
        memoryIncrease: '20%'
      },
      rollbackCriteria: [
        'Manager Dashboard load > 3s',
        'Timeline render > 1s',
        'Error rate > 5%',
        'RLS policy bypass detected',
        'Permission check failures'
      ],
      canary: {
        enabled: false,
        percentage: 10,
        gates: {
          errorRate: 0.05,
          p95Latency: 2000,
          cacheHitRate: 0.80
        }
      }
    },
    
    // ========================================
    // ✅ RETIRED 2026-05-11 (PR-H1): ENABLE_CRM + ENABLE_CRM_MOCKUP_DATA metadata (orphans)
    // ========================================

    // ✅ REMOVED 2026-04-30 (PR-E): ENABLE_STAKEHOLDER_CRM_DASHBOARD metadata block (hardcoded; dashboard always on)
    
    ENABLE_SAFE_NAVIGATION_AUTO_RELOAD: {
      enabled: false, // ✅ OFF by default - soft cleanup instead of reload
      owner: 'platform-team',
      expiryDate: '2025-12-31',
      rollbackCommand: 'globalFeatureFlags.disable("ENABLE_SAFE_NAVIGATION_AUTO_RELOAD")',
      kpis: [
        'Unintended reload rate < 0.1%',
        'Navigation timeout rate < 0.5%',
        'Execute artifacts leak < 0.01%'
      ],
      description: 'Controls automatic page reload behavior when navigation issues occur. When OFF, uses soft recovery (DOM cleanup + warnings) instead of hard reload. Prevents data loss during form entry.',
      phase: 'Safe Navigation Optimization',
      performanceBudget: {
        loadingTimeReduction: '0%',
        dbCallReduction: '0%',
        memoryIncrease: '0%'
      },
      rollbackCriteria: [
        'Execute Portal artifacts remain > 0.5% sessions',
        'Navigation stuck > 1% sessions',
        'Overlay leaks reported by users'
      ]
    },
    
    ENABLE_PROGRESSIVE_LAYER_LOADING: {
      enabled: false, // ✅ OFF by default, canary rollout
      owner: 'diary-team',
      expiryDate: '2025-12-31',
      rollbackCommand: 'globalFeatureFlags.disable("ENABLE_PROGRESSIVE_LAYER_LOADING")',
      kpis: [
        'First Layer Visible: < 1s (down from 5-10s)',
        'Background preload: Complete within 10s',
        'Error rate: < 1%',
        'Memory increase: < 10MB'
      ],
      description: 'Load current layer first, preload others in background. Non-blocking UI.',
      phase: 'Execute Tab Optimization',
      performanceBudget: {
        loadingTimeReduction: '80%', // Perceived load time: 5s → 1s
        dbCallReduction: '0%',
        memoryIncrease: '5%'
      },
      rollbackCriteria: [
        'First layer load time > 2s',
        'Background preload fails silently',
        'Memory leak detected'
      ]
    },
    
    ENABLE_OPTIMIZED_DRAWING_UPLOADS: {
      enabled: false, // ✅ OFF by default, canary rollout
      owner: 'drawing-team',
      expiryDate: '2025-12-31',
      rollbackCommand: 'globalFeatureFlags.disable("ENABLE_OPTIMIZED_DRAWING_UPLOADS")',
      kpis: [
        'Upload time: 1-2s → 2-3s (+1s one-time cost)',
        'Load time: 2-3s → 0.8s (-70%)',
        'Bandwidth: 2-5 MB → 50 KB (-80%)',
        'Security: MIME sniffing ✅'
      ],
      description: 'Apply layer image optimization to drawing uploads (MIME sniff, resize, compress, thumbnail). Reduces load time by 70% and bandwidth by 80%.',
      phase: 'Phase 1 - Drawing Optimization',
      performanceBudget: {
        loadingTimeReduction: '70%',
        dbCallReduction: '0%',
        memoryIncrease: '5%'
      },
      rollbackCriteria: [
        'Error rate > 5%',
        'Upload time > 8s (P95)',
        'Thumbnail generation failures > 2%',
        'User complaints about slow uploads'
      ],
      canary: {
        enabled: false,
        percentage: 0,
        gates: {
          errorRate: 0.01,      // 1% max error
          p95Latency: 8000,     // 8s max upload time
          cacheHitRate: 0.0     // N/A for uploads
        }
      }
    },
    
    ENABLE_DRAWING_PDF_QUALITY_SETTINGS: {
      enabled: true, // ✅ Enabled for production - users can choose PDF quality
      owner: 'drawing-module',
      expiryDate: '2025-12-31',
      rollbackCommand: 'globalFeatureFlags.disable("ENABLE_DRAWING_PDF_QUALITY_SETTINGS")',
      kpis: [
        { 
          metric: 'export_duration', 
          threshold: { min: 0, max: 30000 }, 
          unit: 'ms' 
        },
        { 
          metric: 'file_size', 
          threshold: { min: 0, max: 10485760 }, // 10MB
          unit: 'bytes' 
        }
      ],
      description: 'Cho phép user chọn chất lượng PDF export (Low/Medium/High/Ultra) để cân bằng giữa quality và performance',
      phase: 'drawing-optimization',
      performanceBudget: {
        loadingTimeReduction: '0%', // Client-side only
        dbCallReduction: '0%',
        memoryIncrease: '< 2%' // Minimal config storage
      },
      rollbackCriteria: [
        'Export failure rate > 1%',
        'Export time > 60s for Ultra quality',
        'User complaints về file size quá lớn'
      ]
    },
    // ✅ REMOVED: ENABLE_OPTIMIZED_LAYER_IMAGES (hardcoded 2025-11-09)
    
    // ========================================
    // SPECS REPORT V2: 3-LAYER ARCHITECTURE
    // ========================================
    ENABLE_SPECS_REPORT_V2: {
      enabled: false, // ✅ OFF by default, canary rollout
      owner: 'diary-team',
      expiryDate: '2025-12-31',
      rollbackCommand: 'globalFeatureFlags.disable("ENABLE_SPECS_REPORT_V2")',
      kpis: [
        'Direct PDF export (no manual print)',
        '1:1 A4 @96DPI preview accuracy',
        'Header/footer customization',
        'Grouping: area/type/supplier/status',
        'Locale support: vi-VN/en-US',
        'Print CSS: Robust page breaks',
        'Lazy load html2pdf.js: +200KB bundle (lazy)',
        'Preview thumbnail, Export high-res'
      ],
      description: 'Specs Report V2 with 3-Layer Architecture: Settings (single source of truth), Live Preview (1:1 A4 @96DPI), PDF Export (html2pdf.js). Includes advanced grouping, header/footer, robust page breaks, and XSS protection.',
      phase: 'Phase 3 - Integration',
      performanceBudget: {
        loadingTimeReduction: '0%', // No impact on initial load (lazy)
        dbCallReduction: '0%',
        memoryIncrease: '5%' // html2pdf.js lazy loaded only during export
      },
      rollbackCriteria: [
        'PDF export error rate > 5%',
        'Export timeout > 60s (P95)',
        'Page break issues reported > 2%',
        'CORS image loading failures > 10%',
        'User complaints about PDF quality'
      ],
      canary: {
        enabled: false,
        percentage: 0,
        gates: {
          errorRate: 0.05,      // 5% max error
          p95Latency: 60000,    // 60s max export time
          cacheHitRate: 0.0     // N/A for PDF export
        }
      }
    },
    
    // ========================================
    // API OPTIMIZATION: Progressive Improvements (Light QA Contract)
    // ========================================
    ENABLE_API_QUERY_KEY_CONSOLIDATION: {
      enabled: false, // ❌ OFF by default
      owner: 'api-optimization-team',
      expiryDate: '2025-12-31',
      rollbackCommand: 'globalFeatureFlags.disable("ENABLE_API_QUERY_KEY_CONSOLIDATION")',
      kpis: [
        { metric: 'code_duplication_lines', threshold: -30, operator: '<' as const },
        { metric: 'bundle_size_kb', threshold: -0.5, operator: '<' as const },
        { metric: 'query_key_conflicts', threshold: 0, operator: '=' as const }
      ],
      description: 'Remove dead query keys and consolidate to module-specific pattern',
      phase: 'api-optimization-phase-1',
      performanceBudget: {
        loadingTimeReduction: '0%',
        dbCallReduction: '0%',
        memoryIncrease: '0%'
      },
      rollbackCriteria: [
        'TypeScript compilation errors',
        'Query key not found warnings',
        'Cache invalidation failures'
      ]
    },
    
    ENABLE_API_OPTIMISTIC_UPDATES: {
      enabled: false, // ❌ OFF by default
      owner: 'api-optimization-team',
      expiryDate: '2026-01-31',
      rollbackCommand: 'globalFeatureFlags.disable("ENABLE_API_OPTIMISTIC_UPDATES")',
      kpis: [
        { metric: 'ui_update_latency_ms', threshold: 50, operator: '<' as const },
        { metric: 'perceived_performance_improvement', threshold: 30, operator: '>' as const },
        { metric: 'rollback_success_rate', threshold: 100, operator: '=' as const }
      ],
      description: 'Enable instant UI updates for mutations with automatic rollback on error',
      phase: 'api-optimization-phase-2',
      performanceBudget: {
        loadingTimeReduction: '100%', // UI latency
        dbCallReduction: '0%',
        memoryIncrease: '2%' // Snapshot storage
      },
      rollbackCriteria: [
        'Rollback failure rate > 1%',
        'Data inconsistency detected',
        'Memory leak (>50MB increase)',
        'User reports of phantom data'
      ],
      canary: {
        enabled: false,
        percentage: 10,
        gates: {
          errorRate: 0.01,
          p95Latency: 500,
          cacheHitRate: 0.80
        }
      }
    },
    
    // ✅ RETIRED 2026-05-11 (PR-H1): ENABLE_API_TYPE_EXPORTS metadata (orphan)
    
    ENABLE_ROADMAP_CATEGORIES_DRAFT_PERSISTENCE: {
      enabled: false, // ❌ OFF by default (Light QA Contract)
      owner: 'roadmap-team',
      expiryDate: '2026-06-30',
      rollbackCommand: 'globalFeatureFlags.disable("ENABLE_ROADMAP_CATEGORIES_DRAFT_PERSISTENCE"); roadmapRollback.disableAll()',
      kpis: [
        { metric: 'draft_save_success_rate', threshold: 99, operator: '>' as const },
        { metric: 'draft_save_latency_p95_ms', threshold: 100, operator: '<' as const },
        { metric: 'draft_load_latency_p95_ms', threshold: 50, operator: '<' as const },
        { metric: 'localstorage_quota_error_rate', threshold: 1, operator: '<' as const }
      ],
      rollbackCriteria: [
        'Draft save error rate > 1%',
        'localStorage quota exceeded > 1%',
        'LCP regression > 3s',
        'User complaints > 5 in 24h'
      ],
      description: 'Prevents data loss in Manage Roadmap Categories dialog by persisting form state to localStorage',
      phase: 'Roadmap UX Enhancement',
      performanceBudget: {
        loadingTimeReduction: '0%',
        dbCallReduction: '0%',
        memoryIncrease: '<1%' // +2KB gzipped bundle
      }
    },
    
    ENABLE_ITEM_TYPE_FILTERING: {
      enabled: true, // ✅ ENABLED for testing (Light QA Contract compliance)
      owner: 'items-team',
      expiryDate: '2025-12-31',
      rollbackCommand: 'globalFeatureFlags.disable("ENABLE_ITEM_TYPE_FILTERING")',
      kpis: [
        { metric: 'service_plan_filter_accuracy', threshold: 100, operator: '=' as const },
        { metric: 'build_plan_filter_accuracy', threshold: 100, operator: '=' as const },
        { metric: 'query_performance_p95_ms', threshold: 100, operator: '<' as const },
        { metric: 'error_rate', threshold: 0.1, operator: '<' as const }
      ],
      description: 'Enables item_type column filtering to separate build items from service items in different views',
      phase: 'item-type-phase1',
      performanceBudget: {
        loadingTimeReduction: '0%', // Neutral impact expected
        dbCallReduction: '0%',      // Same query count
        memoryIncrease: '2%'        // Minimal increase for new field
      },
      rollbackCriteria: [
        'Filter accuracy < 95%',
        'Query time > 200ms',
        'Error rate > 1%',
        'User reports of missing items'
      ],
      canary: {
        enabled: false,
        percentage: 0,
        gates: {
          errorRate: 0.01,      // 1% max error rate
          p95Latency: 200,      // 200ms max P95
          cacheHitRate: 0.80    // 80% min cache hit
        }
      }
    },
    
    // ✅ RETIRED 2026-05-01 (PR-F3): ENABLE_TIMELINE_REPORT_ASSESSMENT metadata removed
    
    ENABLE_SIMPLE_PERMISSION_MANAGER: {
      enabled: true,
      owner: 'admin-team',
      expiryDate: '2026-01-31',
      rollbackCommand: 'globalFeatureFlags.disable("ENABLE_SIMPLE_PERMISSION_MANAGER")',
      kpis: [],
      description: 'New simplified permission manager with real-time sync for all 231 permissions',
      phase: 'admin-permission-v2',
      performanceBudget: {
        loadingTimeReduction: '0%',
        dbCallReduction: '0%',
        memoryIncrease: '0%'
      },
      rollbackCriteria: [
        'Toggle không hoạt động',
        'Data không sync với DB',
        'Error rate > 1%'
      ],
      canary: {
        enabled: false,
        percentage: 0,
        gates: {
          errorRate: 0.01,
          p95Latency: 1000,
          cacheHitRate: 0.80
        }
      }
    },
    
    ENABLE_GDRIVE_PROJECT_NAME_FOLDERS: {
      enabled: false, // ❌ OFF by default (Light QA Contract)
      owner: 'docs-team',
      expiryDate: '2025-12-31',
      rollbackCommand: 'globalFeatureFlags.disable("ENABLE_GDRIVE_PROJECT_NAME_FOLDERS")',
      kpis: [
        { metric: 'folder_rename_success_rate', threshold: 99, operator: '>' as const, unit: '%' },
        { metric: 'folder_creation_duration_ms', threshold: 3000, operator: '<' as const, unit: 'ms' },
        { metric: 'gdrive_api_error_rate', threshold: 1, operator: '<' as const, unit: '%' }
      ],
      description: 'Use sanitized project names for Google Drive folders instead of project-{id}. Root: ENP_Binh_Duong, Subfolders: ENP_Binh_Duong_DOC. Includes migration function for existing projects.',
      phase: 'docs-gdrive-naming-v1',
      performanceBudget: {
        loadingTimeReduction: '0%',
        dbCallReduction: '0%',
        memoryIncrease: '< 1%'
      },
      rollbackCriteria: [
        'Folder creation error rate > 1%',
        'P95 latency > 3s',
        'GDrive API quota exceeded',
        'Duplicate folder names detected',
        'Permission errors > 0.5%'
      ],
      canary: {
        enabled: false,
        percentage: 0,
        gates: {
          errorRate: 0.01,
          p95Latency: 3000,
          cacheHitRate: 0.95
        }
      }
    },
    
    // ✅ RETIRED 2026-05-01 (PR-F2.5a): ENABLE_NEW_GDRIVE_FOLDERS metadata removed (hardcoded true, backend env var preserved)
    
    ENABLE_GDRIVE_PERMISSION_SYNC: {
      enabled: false, // ❌ OFF by default (Light QA Contract compliant)
      owner: 'gdrive-team',
      expiryDate: '2025-12-31',
      rollbackCommand: 'globalFeatureFlags.disable("ENABLE_GDRIVE_PERMISSION_SYNC"); // Instant revert, permissions remain unchanged',
      kpis: [
        { metric: 'sync_success_rate', threshold: 95, operator: '>' as const, unit: '%' },
        { metric: 'latency_p95_per_user', threshold: 3000, operator: '<' as const, unit: 'ms' },
        { metric: 'error_rate', threshold: 1, operator: '<' as const, unit: '%' },
        { metric: 'api_call_rate', threshold: 12000, operator: '<' as const, unit: 'calls/min' }
      ],
      description: 'Automatic Google Drive permission synchronization. Maps DB folder permissions to GDrive roles (reader/writer) with Logic C for _DOC_contract. Manual trigger from admin UI, auto-sync for new project owners.',
      phase: 'gdrive-permission-sync',
      performanceBudget: {
        loadingTimeReduction: '0%', // Backend feature, no client impact
        dbCallReduction: '0%',
        memoryIncrease: '< 1%' // Minimal client-side impact
      },
      rollbackCriteria: [
        'Error rate > 5% (within 1 hour)',
        'P95 latency > 5s per user',
        'Wrong permissions granted > 1%',
        'API quota exceeded',
        'Sync failure rate > 5%'
      ],
      canary: {
        enabled: true,
        percentage: 5,
        gates: {
          errorRate: 0.05, // 5% error rate threshold
          p95Latency: 3000, // 3s latency threshold per user
          cacheHitRate: 0.80 // Not applicable but required
        }
      }
    },
    
     // ✅ RETIRED 2026-05-01 (PR-F2): ENABLE_NEW_GDRIVE_FOLDER_PERMISSIONS metadata removed (consolidatedPerms VIEW_FOLDER_* preserved)
    
    
    ENABLE_PL_PERMISSIONS: {
      enabled: false, // ❌ OFF by default để test fallback logic
      owner: 'platform-team',
      expiryDate: '2025-12-31',
      rollbackCommand: 'globalFeatureFlags.disable("ENABLE_PL_PERMISSIONS")',
      kpis: [],
      description: 'Sử dụng PL_ACCESS permission thay vì combo Collection+Procurement',
      phase: 'P&L Permissions',
      performanceBudget: {
        loadingTimeReduction: '0%',
        dbCallReduction: '0%',
        memoryIncrease: '0%'
      },
      rollbackCriteria: ['Permission check fails', 'Tab không hiển thị đúng']
    },
    
    // ✅ RETIRED 2026-04-30 (PR-F1): ENABLE_SITE_MODULE metadata removed
    
    // ✅ RETIRED 2026-05-01 (PR-F3): ENABLE_AI_PLAN_ADVISOR metadata removed
    
    ENABLE_SITE_PHOTO_STATUS: {
      enabled: false, // ❌ OFF by default (canary rollout, Light QA Contract compliant)
      owner: 'site-team',
      expiryDate: '2026-06-30',
      rollbackCommand: 'globalFeatureFlags.disable("ENABLE_SITE_PHOTO_STATUS")',
      kpis: [
        { metric: 'page_lcp', threshold: 2500, operator: '<' as const, unit: 'ms' },
        { metric: 'photo_status_update_duration', threshold: 500, operator: '<' as const, unit: 'ms' },
        { metric: 'error_rate', threshold: 1, operator: '<' as const, unit: '%' }
      ],
      description: 'Thay thế 4 tabs (Máy móc, Vấn đề, Hoàn thành, Nhân công) bằng status cho mỗi ảnh (thiếu thông tin, đang thi công với %, issues, hoàn thành)',
      phase: 'site-photo-status-v1',
      performanceBudget: {
        loadingTimeReduction: '0%',
        dbCallReduction: '0%',
        memoryIncrease: '5%'
      },
      rollbackCriteria: [
        'Error rate > 1%',
        'LCP > 2.5s',
        'Photo status update fails > 5%',
        'UI tabs cũ không hoạt động khi flag OFF'
      ]
    },
    
    // ✅ RETIRED 2026-04-30 (PR-F1): ENABLE_SERVICE_ITEM_ASSIGNMENT metadata removed
    
    ENABLE_SITE_LAZY_LOAD_SECTIONS: {
      enabled: false, // ❌ OFF by default (Light QA Contract compliant)
      owner: 'site-team',
      expiryDate: '2026-01-31',
      rollbackCommand: 'globalFeatureFlags.disable("ENABLE_SITE_LAZY_LOAD_SECTIONS")',
      kpis: [
        { metric: 'page_lcp', threshold: 2500, operator: '<' as const, unit: 'ms' },
        { metric: 'initial_api_calls', threshold: 5, operator: '<=' as const },
        { metric: 'error_rate', threshold: 1, operator: '<' as const, unit: '%' }
      ],
      description: 'Lazy load collapsed sections (Team Attendance, Attendance, Labor, Documents) in Site page - only fetch data when user expands section',
      phase: 'site-performance-v1',
      performanceBudget: {
        loadingTimeReduction: '60%',
        dbCallReduction: '60%',
        memoryIncrease: '0%'
      },
      rollbackCriteria: [
        'Error rate > 1%',
        'LCP > 2.5s',
        'Sections không load data khi expand',
        'UI không responsive khi flag OFF'
      ]
    },
    
    // ✅ RETIRED 2026-05-11 (PR-H1): ENABLE_ROADMAP_DELETE_UI metadata (orphan)
    
    // ========================================
    // FORCE REFRESH SYSTEM (Light QA Contract Compliant)
    // ========================================
    ENABLE_FORCE_REFRESH: {
      enabled: false, // ❌ OFF by default (Light QA Contract compliant)
      owner: 'system-admin',
      expiryDate: '2026-03-09',
      rollbackCommand: 'globalFeatureFlags.disable("ENABLE_FORCE_REFRESH")',
      kpis: [
        { metric: 'broadcast_success_rate', threshold: 99, operator: '>' as const, unit: '%' },
        { metric: 'refresh_latency_ms', threshold: 1000, operator: '<' as const, unit: 'ms' }
      ],
      description: 'Force all users (desktop & mobile) to refresh browser for latest app version via Supabase Realtime broadcast',
      phase: 'force-refresh-v1',
      performanceBudget: {
        loadingTimeReduction: '0%',
        dbCallReduction: '0%',
        memoryIncrease: '2%'
      },
      rollbackCriteria: [
        'Error rate > 1%',
        'Broadcast fails > 5%',
        'App không hoạt động khi flag OFF',
        'Memory leak detected'
      ]
    },
    
    // ========================================
    // HR MODULE (Light QA Contract Compliant)
    // ========================================
    ENABLE_HR_MODULE: {
      enabled: false, // ❌ OFF by default (Light QA Contract compliant)
      owner: 'hr-team',
      expiryDate: '2026-06-30',
      rollbackCommand: 'globalFeatureFlags.disable("ENABLE_HR_MODULE")',
      kpis: [
        { metric: 'page_lcp', threshold: 2500, operator: '<' as const, unit: 'ms' },
        { metric: 'error_rate', threshold: 1, operator: '<' as const, unit: '%' }
      ],
      description: 'HR Module với 6-tab employee profile: Cá nhân, Công việc, Lương, Bảo hiểm, Gia đình, Tài sản. Routes: /hr/employee/:id',
      phase: 'hr-module-phase-2',
      performanceBudget: {
        loadingTimeReduction: '0%',
        dbCallReduction: '0%',
        memoryIncrease: '5%'
      },
      rollbackCriteria: [
        'Error rate > 1%',
        'LCP > 2.5s',
        'RLS violations detected',
        'App cũ không hoạt động khi flag OFF'
      ]
    },
    
    // ========================================
    // PROJECT ROLES (Light QA Contract Compliant)
    // ========================================
    ENABLE_PROJECT_ROLES: {
      enabled: false, // ❌ OFF by default (Light QA Contract compliant)
      owner: 'project-team',
      expiryDate: '2026-06-30',
      rollbackCommand: 'globalFeatureFlags.disable("ENABLE_PROJECT_ROLES")',
      kpis: [
        { metric: 'page_lcp', threshold: 2500, operator: '<' as const, unit: 'ms' },
        { metric: 'error_rate', threshold: 1, operator: '<' as const, unit: '%' }
      ],
      description: 'Project-specific roles (SOL/PM/CD/QS/SM) in Resource tab. Filter projects by user roles.',
      phase: 'project-roles-phase-1',
      performanceBudget: {
        loadingTimeReduction: '0%',
        dbCallReduction: '0%',
        memoryIncrease: '2%'
      },
      rollbackCriteria: [
        'Error rate > 1%',
        'LCP > 2.5s',
        'App cũ không hoạt động khi flag OFF'
      ]
    },
    
    // ========================================
    // PEOPLE MODULE (Gradual Migration)
    // ========================================
    ENABLE_PEOPLE_MODULE: {
      enabled: false, // ❌ OFF by default (gradual rollout)
      owner: 'hr-team',
      expiryDate: '2026-06-30',
      rollbackCommand: 'globalFeatureFlags.disable("ENABLE_PEOPLE_MODULE")',
      kpis: [
        { metric: 'page_lcp', threshold: 2500, operator: '<' as const, unit: 'ms' },
        { metric: 'error_rate', threshold: 1, operator: '<' as const, unit: '%' }
      ],
      description: 'Unified People Module merging Roadmap + HR. Shows "People" menu item instead of separate Roadmap/HR.',
      phase: 'people-module-phase-1',
      performanceBudget: {
        loadingTimeReduction: '10%',
        dbCallReduction: '20%',
        memoryIncrease: '0%'
      },
      rollbackCriteria: [
        'Error rate > 1%',
        'LCP > 2.5s',
        'Old /roadmap or /hr routes broken',
        'Employee data not showing'
      ]
    },
    
    // ========================================
    // PERMIT TAB (Light QA Contract Compliant)
    // ========================================
     // ✅ RETIRED 2026-05-01 (PR-F2): ENABLE_PERMIT_TAB metadata removed (PERMIT_VIEW/CREATE/EDIT/DELETE preserved)
     // ✅ RETIRED 2026-05-01 (PR-F2): ENABLE_PERMIT_SHARE metadata removed (PERMIT_SHARE preserved)
     // ✅ RETIRED 2026-05-01 (PR-F2): ENABLE_MEETING_MODULE metadata removed (hasMeetingAccess preserved)
    
    
    // ✅ RETIRED 2026-05-09 (PR-G6): ENABLE_AI_TOOL_CALLING (orphan, zero functional consumers, AI_TOOL_CALLING_ACCESS permission preserved, storageVersion bumped v16→v17)
    
    // 🪦 RETIRED 2026-05-20 (PR-H2): ENABLE_AI_PROJECT_ADVISOR metadata removed — orphan, 0 consumers
    // 🪦 RETIRED 2026-05-20 (PR-H2): ENABLE_AI_STAKEHOLDER_ADVISOR metadata removed — orphan, 0 consumers
    // 🪦 RETIRED 2026-05-20 (PR-H2): ENABLE_AI_PEOPLE_ADVISOR metadata removed — orphan, 0 consumers
    
    // ✅ RETIRED 2026-04-30 (PR-F1): ENABLE_NEEDS_TAB metadata removed
    // ✅ RETIRED 2026-04-30 (PR-F1): ENABLE_QAQC_TAB metadata removed
    
    // ✅ CLOSING TAB: Project closing summary with evaluation and key learnings (OFF by default, Light QA Contract compliant)
    ENABLE_CLOSING_TAB: {
      enabled: false, // ❌ OFF by default - Light QA Contract
      owner: 'project-team',
      expiryDate: '2026-06-30',
      rollbackCommand: 'globalFeatureFlags.disable("ENABLE_CLOSING_TAB")',
      kpis: [
        { metric: 'page_lcp', threshold: 2500, operator: '<' as const, unit: 'ms' },
        { metric: 'error_rate', threshold: 1, operator: '<' as const, unit: '%' }
      ],
      description: 'Tab tổng kết dự án với performance metrics, team responsibility, subcon evaluation, key learnings',
      phase: 'closing-tab-v1',
      performanceBudget: {
        loadingTimeReduction: '0%',
        dbCallReduction: '0%',
        memoryIncrease: '5%'
      },
      rollbackCriteria: [
        'Error rate > 1%',
        'Permission check fails',
        'Data persistence issues'
      ]
    },
    
    // ✅ CONTROL CENTER Phase 1: Activity Log
    ENABLE_ACTIVITY_LOG: {
      enabled: true, // ✅ ON - Activity Log active
      owner: 'control-center-team',
      expiryDate: '2026-12-31',
      rollbackCommand: 'globalFeatureFlags.disable("ENABLE_ACTIVITY_LOG")',
      kpis: [
        { metric: 'page_lcp', threshold: 2500, operator: '<' as const, unit: 'ms' },
        { metric: 'error_rate', threshold: 1, operator: '<' as const, unit: '%' }
      ],
      description: 'Activity Log tab - unified audit trail for all project modules',
      phase: 'control-center-phase-1',
      performanceBudget: {
        loadingTimeReduction: '0%',
        dbCallReduction: '0%',
        memoryIncrease: '5%'
      },
      rollbackCriteria: [
        'Error rate > 1%',
        'Trigger performance degradation',
        'Data consistency issues'
      ]
    },
    
    // ✅ CONTROL CENTER Phase 2: Sign-off System
    ENABLE_SIGN_OFF_SYSTEM: {
      enabled: true, // ✅ ON - Sign-off system active
      owner: 'control-center-team',
      expiryDate: '2026-12-31',
      rollbackCommand: 'globalFeatureFlags.disable("ENABLE_SIGN_OFF_SYSTEM")',
      kpis: [
        { metric: 'page_lcp', threshold: 2500, operator: '<' as const, unit: 'ms' },
        { metric: 'error_rate', threshold: 1, operator: '<' as const, unit: '%' }
      ],
      description: 'Sign-off system with traffic light indicators (red/yellow/green) for project entities',
      phase: 'control-center-phase-2',
      performanceBudget: {
        loadingTimeReduction: '0%',
        dbCallReduction: '0%',
        memoryIncrease: '5%'
      },
      rollbackCriteria: [
        'Error rate > 1%',
        'Sign-off data inconsistency',
        'Performance degradation on entity lists'
      ]
    },
    
    // ✅ SITE FORMS: Dynamic forms/requests system
    ENABLE_SITE_FORMS: {
      enabled: false, // ❌ OFF by default (Light QA Contract)
      owner: 'site-team',
      expiryDate: '2026-12-31',
      rollbackCommand: 'globalFeatureFlags.disable("ENABLE_SITE_FORMS")',
      kpis: [
        { metric: 'page_lcp', threshold: 2500, operator: '<' as const, unit: 'ms' },
        { metric: 'error_rate', threshold: 1, operator: '<' as const, unit: '%' }
      ],
      description: 'Dynamic forms/requests system (đơn từ) in Site module with sign-off workflow',
      phase: 'site-forms',
      performanceBudget: {
        loadingTimeReduction: '0%',
        dbCallReduction: '0%',
        memoryIncrease: '3%'
      },
      rollbackCriteria: [
        'Error rate > 1%',
        'Form submission failures',
        'Performance degradation on Site page'
      ]
    },
    
    ENABLE_PAYMENT_MILESTONES_OPTIMIZED_QUERY: {
      enabled: false, // ❌ OFF by default (Light QA Contract)
      owner: 'procurement-team',
      expiryDate: '2025-12-31',
      rollbackCommand: 'globalFeatureFlags.disable("ENABLE_PAYMENT_MILESTONES_OPTIMIZED_QUERY")',
      kpis: [
        { metric: 'latency_p95', threshold: 500, operator: '<' as const },
        { metric: 'payload_size', threshold: 500, operator: '<' as const },
        { metric: 'error_rate', threshold: 0.01, operator: '<' as const }
      ],
      rollbackCriteria: [
        'Latency P95 > 800ms (worse than legacy)',
        'Error rate > 1%',
        'Missing milestones (data inconsistency)',
        'Payload size > 600KB'
      ],
      description: 'Consolidates dual queries (payment_date + paid_date) into 1 RPC call with security hardening',
      phase: 'api-optimization-phase1',
      performanceBudget: {
        loadingTimeReduction: '50%',
        dbCallReduction: '50%',
        memoryIncrease: '0%'
      }
    },
    
    ENABLE_CONSOLIDATED_PROCUREMENT_API: {
      enabled: false, // ❌ OFF by default (Light QA Contract)
      owner: 'procurement-team',
      expiryDate: '2025-12-31',
      rollbackCommand: 'globalFeatureFlags.disable("ENABLE_CONSOLIDATED_PROCUREMENT_API")',
      kpis: [
        { metric: 'latency_p95', threshold: 500, operator: '<' as const },
        { metric: 'payload_size', threshold: 400, operator: '<' as const },
        { metric: 'error_rate', threshold: 0.01, operator: '<' as const },
        { metric: 'api_calls', threshold: 1, operator: '=' as const }
      ],
      rollbackCriteria: [
        'Latency P95 > 800ms (worse than legacy 3-call pattern)',
        'Error rate > 1%',
        'Data inconsistency (missing items/payments)',
        'Payload size > 600KB'
      ],
      description: 'Consolidates 3 RPC calls (procurement_data + payment_summary + orders) into 1 unified API call',
      phase: 'api-optimization-phase1',
      performanceBudget: {
        loadingTimeReduction: '58%', // 1200ms → 500ms
        dbCallReduction: '66%', // 3 calls → 1 call
        memoryIncrease: '0%'
      }
    },
    
    // ✅ RETIRED 2026-05-01 (PR-F3): ENABLE_PUNCHLIST_LIGHT_V2 metadata removed
    
    ENABLE_EXECUTE_REPORT_STATIC_MILESTONES: {
      enabled: false, // ❌ OFF by default (Light QA Contract)
      owner: 'system',
      expiryDate: '2025-12-15',
      rollbackCommand: 'globalFeatureFlags.disable("ENABLE_EXECUTE_REPORT_STATIC_MILESTONES")',
      kpis: [
        { metric: 'error_rate', threshold: 0.01, operator: '<' as const },
        { metric: 'pdf_export_time', threshold: 3000, operator: '<' as const }
      ],
      description: 'Pre-fetch milestone data for Execute Report PDF export (fix print context data loss)',
      phase: 'bug-fix',
      performanceBudget: {
        loadingTimeReduction: '0%',
        dbCallReduction: '0%',
        memoryIncrease: '< 5%'
      },
      rollbackCriteria: [
        'Milestones not appearing in PDF',
        'PDF export time > 5s',
        'Error rate > 5%',
        'Memory leak detected'
      ]
    },
    
    ENABLE_EXECUTE_REPORT_SORT_BY_START_DATE: {
      enabled: false, // ❌ OFF by default (Light QA Contract)
      owner: 'system',
      expiryDate: '2025-12-15',
      rollbackCommand: 'globalFeatureFlags.disable("ENABLE_EXECUTE_REPORT_SORT_BY_START_DATE")',
      kpis: [
        { metric: 'error_rate', threshold: 0.01, operator: '<' as const },
        { metric: 'pdf_export_time', threshold: 3000, operator: '<' as const }
      ],
      description: 'Sort items by start date in Execute Report PDF for better readability',
      phase: 'bug-fix',
      performanceBudget: {
        loadingTimeReduction: '0%',
        dbCallReduction: '0%',
        memoryIncrease: '< 1%'
      },
      rollbackCriteria: [
        'Sort errors in PDF',
        'Invalid date handling issues',
        'PDF export time > 3s',
        'Error rate > 1%'
      ]
    }
  } as any;

  private storageKey = 'global_feature_flags_v2';
  // PR-H1 (2026-05-11): retired 6 admin panel residual orphans —
  // ENABLE_ADMIN_STAKEHOLDER_QUERIES, ENABLE_ENHANCED_ADMIN_MONITORING,
  // ENABLE_ROADMAP_DELETE_UI, ENABLE_CRM, ENABLE_CRM_MOCKUP_DATA,
  // ENABLE_API_TYPE_EXPORTS. Zero P1/P2/P3 functional consumers verified
  // via PR-H1 audit 9-chunk grep. See QC-HANDOFF v8.6.
  private storageVersion = 18; // ✅ Bumped to v18 (PR-H1 2026-05-11) — retire 6 admin panel residual orphans (PR-G6 also reset state v17, comment carry-forward fix)
  private metricsKey = 'feature_flag_metrics_v1';
  private listeners: Array<() => void> = [];

  constructor() {
    this.initializeFlags();
    this.loadFromStorage();
    
    // Force-enable production critical flags
    if (import.meta.env.MODE === 'production') {
      const forcedFlags: Array<keyof GlobalFeatureFlags> = [
        // ✅ REMOVED 2026-04-30 (PR-C): 'ENABLE_ENHANCED_MONITORING',
        // ✅ RETIRED 2026-05-01 (PR-F3): ENABLE_TIMELINE_REPORT_ASSESSMENT, ENABLE_AI_PLAN_ADVISOR (hardcoded true in consumers)
      ];
      forcedFlags.forEach(flag => {
        if (!this.flags[flag]?.enabled) {
          console.info(`[FeatureFlags] Force-enabling production flag: ${flag}`);
          this.flags[flag].enabled = true;
        }
      });
      this.saveToStorage();
    }
  }

  private initializeFlags() {
    // Initialize with default values for now - metadata can be added later
    const defaultFlags: Record<string, Partial<FlagMetadata>> = {};
    
    // All flags start disabled except diary/realtime (already enabled)
    Object.keys({} as GlobalFeatureFlags).forEach(flag => {
      defaultFlags[flag] = {
        enabled: false,
        owner: 'platform-team',
        rollbackCommand: `globalFeatureFlags.disable("${flag}")`,
        kpis: [],
        description: flag
      };
    });

    // ✅ Removed diary flags (hardcoded since 2025-09)

    // Enable realtime optimization flags
    ['ENABLE_REALTIME_BACKPRESSURE', 'ENABLE_BATCH_INVALIDATION', 'ENABLE_OPTIMIZED_REALTIME_SYNC'].forEach(flag => {
      if (defaultFlags[flag]) defaultFlags[flag].enabled = true;
    });

    // ✅ Removed global tabs (hardcoded since 2025-09)

    // ✅ Safety: Don't wipe existing flag definitions
    if (Object.keys(this.flags).length > 0) return;
    this.flags = defaultFlags as any;
  }

  // ✅ Critical flags that CANNOT be disabled by localStorage
  private readonly FORCED_ON_FLAGS: ReadonlyArray<keyof GlobalFeatureFlags> = [
    // ✅ REMOVED 2026-04-30 (PR-A): ENABLE_PRINT_PIN_LAYOUT_FIX
    // ✅ REMOVED: ENABLE_CUSTOM_MILESTONES (hardcoded 2025-11-09)
     // ✅ RETIRED 2026-05-01 (PR-F3): ENABLE_PUNCHLIST_LIGHT_V2 (hardcoded true, no active consumers)
    // ✅ REMOVED 2026-04-30 (PR-B): ENABLE_TIMEZONE_AWARE_FILTERING, STRICT_PERMISSION_UI (hardcoded true in consumers)
    // ✅ RETIRED 2026-05-01 (PR-F2): ENABLE_NEW_GDRIVE_FOLDER_PERMISSIONS (consolidatedPerms VIEW_FOLDER_* preserved)
    // ✅ RETIRED 2026-04-30 (PR-F1): ENABLE_SITE_MODULE, ENABLE_SERVICE_ITEM_ASSIGNMENT, ENABLE_NEEDS_TAB, ENABLE_QAQC_TAB
    'ENABLE_SITE_PHOTO_STATUS', // ✅ Force ON - Status-based photo UI (2025-11-28)
    'ENABLE_SITE_LAZY_LOAD_SECTIONS', // ✅ Force ON - Lazy load Site sections (2025-12-04)
    // ✅ RETIRED 2026-05-01 (PR-F2): ENABLE_PERMIT_TAB, ENABLE_PERMIT_SHARE, ENABLE_MEETING_MODULE (permission-gated)
  ];

  private migrateFromV1(): void {
    const oldData = localStorage.getItem('global_feature_flags');
    if (oldData) {
      try {
        const parsed = JSON.parse(oldData);
        console.info('[FeatureFlags] Migrating from v1 to v2...');
        
        // Merge old flags
        Object.keys(parsed).forEach(key => {
          if (this.flags[key as keyof GlobalFeatureFlags]) {
            this.flags[key as keyof GlobalFeatureFlags].enabled = parsed[key];
          }
        });
        
        this.saveToStorage();
        localStorage.removeItem('global_feature_flags'); // Cleanup old version
        console.info('[FeatureFlags] Migration complete');
      } catch (error) {
        console.error('[FeatureFlags] Migration failed:', error);
      }
    }
  }

  private loadFromStorage(): void {
    try {
      let stored = localStorage.getItem(this.storageKey);
      
      // ✅ Fallback to sessionStorage backup if localStorage empty
      if (!stored) {
        const backup = sessionStorage.getItem(this.storageKey + '_backup');
        if (backup) {
          console.warn('[FeatureFlags] Recovered from sessionStorage backup');
          try {
            localStorage.setItem(this.storageKey, backup);
            stored = backup;
          } catch (quotaError) {
            console.error('[FeatureFlags] localStorage quota exceeded during recovery:', quotaError);
            stored = backup; // Use from sessionStorage
          }
        } else {
          // Try migrating from v1
          this.migrateFromV1();
          return;
        }
      }
      
      const data = JSON.parse(stored);
      
      // ✅ Validate age (auto-reset if > 30 days old)
      const age = Date.now() - (data.savedAt || 0);
      if (age > 30 * 24 * 60 * 60 * 1000) {
        console.warn('[FeatureFlags] Data too old, resetting');
        this.saveToStorage();
        return;
      }
      
      // Validate version
      if (data.version !== this.storageVersion) {
        console.warn(`[FeatureFlags] Version mismatch (expected ${this.storageVersion}, got ${data.version}), resetting`);
        this.saveToStorage();
        return;
      }
      
      // Validate structure
      if (!data.flags || typeof data.flags !== 'object') {
        console.warn('[FeatureFlags] Invalid structure, resetting');
        this.saveToStorage();
        return;
      }
      
      // Merge with defaults (prevent missing flags)
      Object.keys(this.flags).forEach(key => {
        const flag = key as keyof GlobalFeatureFlags;
        if (data.flags.hasOwnProperty(flag) && typeof data.flags[flag] === 'boolean') {
          this.flags[flag].enabled = data.flags[flag];
        }
      });
      
      // ✅ ALWAYS enforce forced flags ON
      this.FORCED_ON_FLAGS.forEach(flag => {
        if (!this.flags[flag]) {
          this.flags[flag] = {
            enabled: true,
            owner: 'platform-team',
            rollbackCommand: `globalFeatureFlags.disable("${flag}")`,
            kpis: [],
            description: String(flag),
          } as any;
        }
        this.flags[flag].enabled = true;
      });
      
      // ✅ ALWAYS re-save after successful load to update backup
      this.saveToStorage();
    } catch (error) {
      console.error('[FeatureFlags] Load failed, using defaults:', error);
      this.saveToStorage();
    }
  }

  private saveToStorage(): void {
    try {
      const enabled: Record<string, boolean> = {};
      Object.entries(this.flags).forEach(([key, metadata]) => {
        enabled[key] = metadata?.enabled ?? false;
      });
      
      const data = {
        version: this.storageVersion,
        flags: enabled,
        savedAt: Date.now()
      };
      
      const serialized = JSON.stringify(data);
      
      // ✅ Validate before save
      if (serialized.length > 5 * 1024 * 1024) {  // 5MB quota check
        console.error('[FeatureFlags] Save failed: Data too large');
        return;
      }
      
      // ✅ Test write
      try {
        localStorage.setItem(this.storageKey, serialized);
        
        // ✅ Verify write success
        const verify = localStorage.getItem(this.storageKey);
        if (verify !== serialized) {
          throw new Error('Write verification failed');
        }
      } catch (quotaError) {
        console.error('[FeatureFlags] localStorage quota exceeded:', quotaError);
        // ✅ Fallback: Save to sessionStorage
        sessionStorage.setItem(this.storageKey + '_backup', serialized);
      }
    } catch (error) {
      console.warn('Failed to save feature flags to storage:', error);
    }
  }

  private notifyListeners(): void {
    this.listeners.forEach(listener => {
      try {
        listener();
      } catch (error) {
        console.error('[FeatureFlags] Listener error:', error);
      }
    });
  }

  subscribe(listener: () => void): () => void {
    this.listeners.push(listener);
    return () => {
      this.listeners = this.listeners.filter(l => l !== listener);
    };
  }

  private recordMetric(flag: keyof GlobalFeatureFlags, action: 'enable' | 'disable'): void {
    try {
      const metrics = JSON.parse(localStorage.getItem(this.metricsKey) || '[]');
      metrics.push({
        flag,
        action,
        timestamp: Date.now(),
        user: 'system'
      });
      if (metrics.length > 1000) {
        metrics.splice(0, metrics.length - 1000);
      }
      localStorage.setItem(this.metricsKey, JSON.stringify(metrics));
    } catch (error) {
      console.warn('Failed to record metric:', error);
    }
  }

  isEnabled(flag: keyof GlobalFeatureFlags): boolean {
    // Check hardcoded flags first - they are always enabled
    if (isHardcodedFlag(String(flag))) {
      return true;
    }
    return this.flags[flag]?.enabled ?? false;
  }

  /**
   * Check if user is in canary rollout
   * @returns true if flag should be enabled for this user
   */
  isInCanary(flagName: keyof GlobalFeatureFlags): boolean {
    const metadata = this.flags[flagName];
    if (!metadata?.canary?.enabled) {
      return metadata?.enabled || false; // Normal flag check
    }

    // ✅ PHASE QA: Check auto-rollback gates before allowing canary
    if (this.shouldAutoRollback(flagName)) {
      console.error(`[FeatureFlags] Auto-rollback triggered for ${flagName} - gates exceeded`);
      this.disable(flagName);
      toast.error(`Feature ${flagName} auto-disabled`, {
        description: 'Performance gates exceeded. Check KPI metrics.'
      });
      return false;
    }

    // Canary mode: probabilistic rollout
    const userHash = this.getUserHash();
    const threshold = metadata.canary.percentage / 100;
    const inCanary = userHash < threshold;

    // Optional: Whitelist check
    if (metadata.canary.userId?.length) {
      const currentUserId = this.getCurrentUserId();
      if (currentUserId && metadata.canary.userId.includes(currentUserId)) {
        return true; // Force-enable for whitelisted users
      }
    }

    return inCanary;
  }

  /**
   * ✅ PHASE QA: Check if canary should auto-rollback based on gates
   * @returns true if any gate is exceeded and flag should be disabled
   */
  private shouldAutoRollback(flagName: keyof GlobalFeatureFlags): boolean {
    const metadata = this.flags[flagName];
    if (!metadata?.canary?.enabled || !metadata.canary.gates) {
      return false;
    }

    const gates = metadata.canary.gates;
    
    // Check error rate gate
    const errorRate = kpiTracker.getLatestValue(flagName, 'filter_error_rate');
    if (errorRate !== null && errorRate > gates.errorRate) {
      console.error(`[FeatureFlags] ${flagName}: Error rate ${errorRate} exceeds gate ${gates.errorRate}`);
      return true;
    }

    // Check P95 latency gate
    const p95Latency = kpiTracker.getLatestValue(flagName, 'filter_computation_time');
    if (p95Latency !== null && p95Latency > gates.p95Latency) {
      console.error(`[FeatureFlags] ${flagName}: P95 latency ${p95Latency}ms exceeds gate ${gates.p95Latency}ms`);
      return true;
    }

    // Check cache hit rate gate (inverted: we want rate to be ABOVE threshold)
    const cacheHitRate = kpiTracker.getLatestValue(flagName, 'cache_hit_rate');
    if (cacheHitRate !== null && cacheHitRate < gates.cacheHitRate) {
      console.error(`[FeatureFlags] ${flagName}: Cache hit rate ${cacheHitRate} below gate ${gates.cacheHitRate}`);
      return true;
    }

    return false;
  }

  private getUserHash(): number {
    // Stable hash based on user ID or session
    const userId = this.getCurrentUserId() || sessionStorage.getItem('canary_session_id');
    if (!userId) {
      const sessionId = Math.random().toString(36);
      sessionStorage.setItem('canary_session_id', sessionId);
      return parseFloat('0.' + sessionId.slice(2, 8));
    }
    
    // Simple hash: convert user ID to 0-1 range
    let hash = 0;
    for (let i = 0; i < userId.length; i++) {
      hash = ((hash << 5) - hash) + userId.charCodeAt(i);
      hash |= 0; // Convert to 32bit integer
    }
    return Math.abs(hash) / 0x7fffffff; // Normalize to 0-1
  }

  private getCurrentUserId(): string | null {
    // Try to get user ID from auth context
    try {
      const authData = localStorage.getItem('supabase.auth.token');
      if (authData) {
        const parsed = JSON.parse(authData);
        return parsed.currentSession?.user?.id || null;
      }
      return null;
    } catch {
      return null;
    }
  }

  /**
   * Admin: Update canary config
   */
  updateCanaryConfig(
    flagName: keyof GlobalFeatureFlags,
    config: Partial<CanaryConfig>
  ): void {
    const metadata = this.flags[flagName];
    if (!metadata) return;

    metadata.canary = {
      ...metadata.canary,
      ...config,
      startedAt: config.enabled ? Date.now() : metadata.canary?.startedAt
    } as CanaryConfig;

    this.saveToStorage();
    this.notifyListeners();
    
    console.info(`[FeatureFlags] Canary config updated for ${flagName}:`, metadata.canary);
  }

  /**
   * Get flag status for UI rendering
   * @returns 'hardcoded' | 'enabled' | 'disabled'
   */
  getUiStatus(flag: keyof GlobalFeatureFlags): 'hardcoded' | 'enabled' | 'disabled' {
    if (isHardcodedFlag(String(flag))) {
      return 'hardcoded';
    }
    return this.flags[flag]?.enabled ? 'enabled' : 'disabled';
  }

  enable(flag: keyof GlobalFeatureFlags, options?: { enabledAt?: number; preserveMetadata?: boolean; owner?: string; reason?: string }): void {
    // Guard: Hardcoded flags cannot be toggled
    if (isHardcodedFlag(flag)) {
      console.warn(
        `[FeatureFlags] ⚠️ Attempted to enable hardcoded flag: ${flag}`,
        '\nThis flag was hardcoded on:', (HARDCODED_FLAGS as any)[flag]?.hardcodedDate,
        '\nReason:', (HARDCODED_FLAGS as any)[flag]?.reason,
        '\nStack:', new Error().stack
      );
      return; // No-op
    }
    
    const previousState = this.flags[flag]?.enabled || false;

    if (this.flags[flag]) {
      const currentMetadata = this.flags[flag];
      this.flags[flag].enabled = true;
      
      // ✅ Preserve existing enabledAt if requested and not explicitly provided
      if (options?.preserveMetadata && currentMetadata.enabledAt && !options.enabledAt) {
        // Keep existing enabledAt
      } else if (options?.enabledAt) {
        // Set specific timestamp (for Official Enable)
        this.flags[flag].enabledAt = options.enabledAt;
      } else if (!currentMetadata.enabledAt) {
        // Set timestamp only if not already set
        this.flags[flag].enabledAt = Date.now();
      }
      
      this.saveToStorage();
      this.recordMetric(flag, 'enable');
      this.notifyListeners();
      
      // Record audit log
      flagAuditLog.recordChange(
        flag,
        'enable',
        previousState,
        true,
        options?.owner || 'admin', // Use provided owner or default to 'admin'
        options?.reason || 'Manual enable via Feature Flag Admin Panel'
      );
      
      console.info(`✅ Feature flag enabled: ${flag}`);
      
      // Dispatch event for React Context to listen
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('featureFlagChanged', { detail: { flag, enabled: true } }));
      }
    }
  }

  disable(flag: keyof GlobalFeatureFlags, options?: { preserveMetadata?: boolean }): void {
    // Guard 1: Check HARDCODED_FLAGS registry
    if (isHardcodedFlag(flag)) {
      console.error(
        `[FeatureFlags] ⛔ BLOCKED: Attempted to disable hardcoded flag: ${flag}`,
        '\nThis flag is permanently enabled (hardcoded)',
        '\nFiles affected:', (HARDCODED_FLAGS as any)[flag]?.files.join(', '),
        '\nStack:', new Error().stack
      );
      toast.error('Cannot modify hardcoded flag', {
        description: `${flag} was hardcoded on ${(HARDCODED_FLAGS as any)[flag]?.hardcodedDate} and cannot be toggled`
      });
      return; // No-op
    }
    
    // Guard 2: Check flag metadata for hardcoded field
    const metadata = this.flags[flag];
    if (metadata?.hardcoded) {
      console.error(
        `[FeatureFlags] ⛔ BLOCKED: ${flag} is hardcoded`,
        '\nHardcoded date:', metadata.hardcodedDate,
        '\nReason:', metadata.hardcodedReason,
        '\nStack:', new Error().stack
      );
      toast.error('Cannot modify hardcoded flag', {
        description: metadata.hardcodedReason || 'This flag is permanently enabled for stability'
      });
      return; // No-op
    }

    // Production protection
    if (isProductionForcedFlag(flag)) {
      const error = `⛔ Cannot disable production flag: ${flag}`;
      console.error('[FeatureFlags]', error, new Error().stack);
      
      toast.error('Cannot disable production flag', {
        description: `${flag} is locked for stability in production`
      });
      return;
    }
    
    if (this.flags[flag]) {
      const oldValue = this.flags[flag].enabled;
      console.warn(`[FeatureFlags] Disabling flag: ${flag}`, new Error().stack);
      this.flags[flag].enabled = false;
      
      // ✅ Preserve enabledAt if requested (for testing without losing official enable timestamp)
      if (!options?.preserveMetadata) {
        delete this.flags[flag].enabledAt;
      }
      
      this.saveToStorage();
      this.recordMetric(flag, 'disable');
      this.notifyListeners();
      
      // Record audit log
      flagAuditLog.recordChange(
        flag,
        'disable',
        oldValue,
        false,
        'admin', // TODO: Get actual user from auth context
        'Manual disable via Feature Flag Admin Panel'
      );
      
      // Dispatch event for React Context to listen
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('featureFlagChanged', { detail: { flag, enabled: false } }));
      }
    }
  }

  toggle(flag: keyof GlobalFeatureFlags): boolean {
    const newValue = !this.isEnabled(flag);
    if (newValue) {
      this.enable(flag);
    } else {
      this.disable(flag);
    }
    return newValue;
  }

  getMetadata(flag: keyof GlobalFeatureFlags): FlagMetadata | undefined {
    return this.flags[flag];
  }

  getAllFlags(): Record<keyof GlobalFeatureFlags, FlagMetadata> {
    return { ...this.flags };
  }

  getStatus(): { enabledCount: number; totalCount: number; flags: Record<string, boolean> } {
    const flags: Record<string, boolean> = {};
    let enabledCount = 0;

    Object.entries(this.flags).forEach(([key, metadata]) => {
      flags[key] = metadata?.enabled ?? false;
      if (metadata?.enabled) enabledCount++;
    });

    return {
      enabledCount,
      totalCount: Object.keys(this.flags).length,
      flags
    };
  }

  checkKPIs(flag: keyof GlobalFeatureFlags): { passed: boolean; results: Array<{ kpi: KPIThreshold; passed: boolean; value: number | null }> } {
    const metadata = this.flags[flag];
    if (!metadata || !metadata.kpis || metadata.kpis.length === 0) {
      return { passed: true, results: [] };
    }

    const results = metadata.kpis.map(kpi => ({
      kpi,
      passed: kpiTracker.checkThreshold(flag, kpi),
      value: kpiTracker.getLatestValue(flag, kpi.metric)
    }));

    return {
      passed: results.every(r => r.passed),
      results
    };
  }

  emergencyRollback(): void {
    Object.keys(this.flags).forEach(flag => {
      this.disable(flag as keyof GlobalFeatureFlags);
    });
    console.error('🚨 Emergency rollback activated - all feature flags disabled');
  }

  // Week-based enablement helpers
  enableWeek1() { 
    console.info('[FeatureFlags] ℹ️ Week 1 flags were hardcoded on 2025-10-10');
    toast.info('Week 1 already enabled', { 
      description: 'These flags were hardcoded and are permanently active (CONSOLIDATED_PERMISSIONS, CURSOR_PAGINATION, SMART_PRELOADING)' 
    });
  }
  
  disableWeek1() {
    console.warn('[FeatureFlags] ⚠️ Cannot disable Week 1 - flags are hardcoded since 2025-10-10');
    toast.warning('Week 1 flags are hardcoded', { 
      description: 'These optimizations are permanently enabled and cannot be toggled' 
    });
  }

  // ✅ REMOVED 2026-04-30 (PR-C): enableWeek2/disableWeek2 (Week 2 fully retired —
  // TIMELINE_VIRTUALIZATION + OPTIMIZED_FILTERS hardcoded; ENHANCED_MONITORING dropped)

  enableWeek3() {
    toast.warning('⚠️ Hardcoded Flags', {
      description: 'Week 3 flags (COMBINED_PROJECT_QUERY, PREDICTIVE_TABS) were hardcoded on 2025-11-09'
    });
  }

  disableWeek3() {
    toast.warning('⚠️ Hardcoded Flags', {
      description: 'Week 3 flags cannot be disabled (hardcoded 2025-11-09)'
    });
  }

  enableWeek5() {
    this.enable('ENABLE_OPTIMIZED_MAINTENANCE_STATE');
    this.enable('ENABLE_MAINTENANCE_PERMISSIONS');
    this.enable('ENABLE_MAINTENANCE_SMART_PAGINATION');
  }

  disableWeek5() {
    this.disable('ENABLE_OPTIMIZED_MAINTENANCE_STATE');
    this.disable('ENABLE_MAINTENANCE_PERMISSIONS');
    this.disable('ENABLE_MAINTENANCE_SMART_PAGINATION');
  }

  enableWeek6() {
    this.enable('ENABLE_CONSOLIDATED_STAKEHOLDER_PERMISSIONS');
    toast.warning('⚠️ Partially Hardcoded', {
      description: 'ENABLE_OPTIMIZED_STAKEHOLDER_QUERIES was hardcoded on 2025-11-09'
    });
  }

  disableWeek6() {
    this.disable('ENABLE_CONSOLIDATED_STAKEHOLDER_PERMISSIONS');
    toast.warning('⚠️ Cannot disable ENABLE_OPTIMIZED_STAKEHOLDER_QUERIES (hardcoded)');
  }

  enableWeek7() {
    this.enable('ENABLE_OPTIMIZED_PREVIEW_MODE');
    this.enable('ENABLE_PREVIEW_COMPONENTS');
  }

  disableWeek7() {
    this.disable('ENABLE_OPTIMIZED_PREVIEW_MODE');
    this.disable('ENABLE_PREVIEW_COMPONENTS');
  }

  enableWeek8() {
    console.info('[FeatureFlags] ℹ️ Week 8 flag (SUBCONS_PERMISSION_CONSOLIDATION) was hardcoded on 2025-10-10');
    toast.info('Week 8 already enabled', { 
      description: 'SUBCONS_PERMISSION_CONSOLIDATION was hardcoded and is permanently active' 
    });
  }

  disableWeek8() {
    console.warn('[FeatureFlags] ⚠️ Cannot disable Week 8 - flag is hardcoded since 2025-10-10');
    toast.warning('Week 8 flag is hardcoded', { 
      description: 'This optimization is permanently enabled and cannot be toggled' 
    });
  }

  enableWeek9() {
    // ✅ PR-E 2026-04-30: ENABLE_ADMIN_ROLE_CONSOLIDATION retired (hardcoded). Only batch updates remain togglable.
    this.enable('ENABLE_BATCH_PERMISSION_UPDATES');
  }

  disableWeek9() {
    // ✅ PR-E 2026-04-30: ENABLE_ADMIN_ROLE_CONSOLIDATION retired (hardcoded). Only batch updates remain togglable.
    this.disable('ENABLE_BATCH_PERMISSION_UPDATES');
  }

  enableWeek10() {
    this.enable('ENABLE_PERMISSION_MATRIX_VIRTUALIZATION');
  }

  disableWeek10() {
    this.disable('ENABLE_PERMISSION_MATRIX_VIRTUALIZATION');
  }

  enableWeek11() {
    this.enable('ENABLE_ADMIN_PROJECT_CONSOLIDATION');
    this.enable('ENABLE_ADMIN_FILTER_OPTIMIZATION');
  }

  disableWeek11() {
    this.disable('ENABLE_ADMIN_PROJECT_CONSOLIDATION');
    this.disable('ENABLE_ADMIN_FILTER_OPTIMIZATION');
  }

  // ✅ RETIRED 2026-05-11 (PR-H1): enableWeek12 / disableWeek12 (sole flag ENABLE_ENHANCED_ADMIN_MONITORING orphan)

  // PHASE 4: High/Medium Priority Pages
  enablePhase4A() {
    this.enable('ENABLE_USER_MANAGEMENT_VIRTUALIZATION');
    this.enable('ENABLE_ADMIN_DASHBOARD_CONSOLIDATION');
    // ✅ RETIRED 2026-05-11 (PR-H1): ENABLE_ADMIN_STAKEHOLDER_QUERIES (orphan)
    this.enable('ENABLE_INDEX_PAGE_OPTIMIZATION');
  }

  disablePhase4A() {
    this.disable('ENABLE_USER_MANAGEMENT_VIRTUALIZATION');
    this.disable('ENABLE_ADMIN_DASHBOARD_CONSOLIDATION');
    // ✅ RETIRED 2026-05-11 (PR-H1): ENABLE_ADMIN_STAKEHOLDER_QUERIES (orphan)
    this.disable('ENABLE_INDEX_PAGE_OPTIMIZATION');
  }

  enablePhase4B() {
    this.enable('ENABLE_CATEGORY_CACHING');
    this.enable('ENABLE_KEYPLAN_OPTIMIZATION');
    this.enable('ENABLE_SCHEDULE_LITE_VIRTUALIZATION');
  }

  disablePhase4B() {
    this.disable('ENABLE_CATEGORY_CACHING');
    this.disable('ENABLE_KEYPLAN_OPTIMIZATION');
    this.disable('ENABLE_SCHEDULE_LITE_VIRTUALIZATION');
  }

  // PHASE 5: Infrastructure & Backend
  // 🪦 RETIRED 2026-05-02 (PR-G3): enablePhase5A/disablePhase5A helpers (5 procurement flags retired)

  enablePhase5B() {
    this.enable('ENABLE_SELECT_FIELD_TRIMMING');
    this.enable('ENABLE_SUBCONS_SMART_PAGINATION');
    this.enable('ENABLE_ITEMS_TAB_PAGINATION');
    toast.warning('⚠️ Hardcoded Flags', {
      description: 'Realtime flags (BACKPRESSURE, BATCH_INVALIDATION, OPTIMIZED_SYNC) hardcoded 2025-11-09'
    });
  }

  disablePhase5B() {
    this.disable('ENABLE_SELECT_FIELD_TRIMMING');
    this.disable('ENABLE_SUBCONS_SMART_PAGINATION');
    this.disable('ENABLE_ITEMS_TAB_PAGINATION');
    toast.warning('⚠️ Cannot disable hardcoded realtime flags');
  }

  // ✅ REMOVED 2026-04-30 (PR-B): enablePhase5C / disablePhase5C (sole flag ENABLE_TIMEZONE_AWARE_FILTERING retired)

  enablePhase5D() {
    toast.warning('⚠️ Hardcoded Flag', {
      description: 'ENABLE_WEEKLY_PLAN_OVERLAP_FILTER was hardcoded on 2025-11-09 and is always enabled'
    });
  }

  disablePhase5D() {
    toast.warning('⚠️ Hardcoded Flag', {
      description: 'ENABLE_WEEKLY_PLAN_OVERLAP_FILTER cannot be disabled (hardcoded)'
    });
  }

  // ========================================
  // PHASE 6: Legacy Diary Flags — fully retired
  // ========================================
  // ✅ REMOVED 2026-04-30 (PR-D): enablePhase6() / disablePhase6() — Phase 6 fully retired
  // (sole flag ENABLE_VO_IN_MILESTONE_CALCULATION hardcoded true in consumers)


  // ========================================
  // PHASE 7: Plan Page Optimizations — fully retired
  // ========================================
  // 🪦 RETIRED 2026-05-02 (PR-G2): enablePhase7() / disablePhase7() — 5 flags retired, 0 consumers.

  // ========================================
  // PHASE 8: Admin Section Enhancements — fully retired
  // ========================================
  // 🪦 RETIRED 2026-05-02 (PR-G1): enablePhase8/disablePhase8 shrunk 13→6 flags.
  // 🪦 RETIRED 2026-05-04 (PR-G5): enablePhase8/disablePhase8 fully removed — 6 remaining flags retired, 0 consumers.

  // ========================================
  // PHASE 22.1: User Visibility Simplification
  // ========================================
  enablePhase22(): void {
    this.enable('ENABLE_SIMPLIFIED_USER_VISIBILITY');
    console.info('[FeatureFlags] Phase 22.1 (User Visibility) enabled: 1 flag');
    toast.success('Phase 22.1 enabled', { 
      description: 'Simplified user visibility: company + project auto-visibility with exception-based hiding' 
    });
  }

  disablePhase22(): void {
    this.disable('ENABLE_SIMPLIFIED_USER_VISIBILITY');
    console.warn('[FeatureFlags] Phase 22.1 (User Visibility) disabled: 1 flag');
    toast.warning('Phase 22.1 disabled', { 
      description: 'Reverted to legacy user visibility (480-row permissions table)' 
    });
  }

  // ✅ REMOVED 2026-04-30 (PR-A): enablePhase22_2 / disablePhase22_2 (flag dropped)

  // Rollback functions
  rollbackWeek(weekId: string): void {
    const config = (FLAG_WEEK_MAPPING as any)[weekId];
    if (!config) {
      console.error(`[FeatureFlags] Unknown week: ${weekId}`);
      return;
    }

    console.warn(`[FeatureFlags] Rolling back ${config.title}...`);
    config.flags.forEach((flag: string) => {
      this.disable(flag as keyof GlobalFeatureFlags);
    });
    
    toast.warning(`Rolled back: ${config.title}`, {
      description: `Disabled ${config.flags.length} flags`
    });
  }

  rollbackPhase(phaseId: number | string): void {
    let weeks: WeekConfig[];
    
    switch (phaseId) {
      case 1:
        weeks = PHASE1_WEEKS;
        break;
      case 2:
        weeks = PHASE2_WEEKS;
        break;
      case 3:
        weeks = PHASE3_WEEKS;
        break;
      case '4A':
      case '4B':
        weeks = PHASE4_CONFIGS.filter(c => c.phase === phaseId);
        break;
      case '5A':
      case '5B':
      case '5C':
      case '5D':
        weeks = PHASE5_CONFIGS.filter(c => c.phase === phaseId);
        break;
      default:
        console.error(`[FeatureFlags] Unknown phase: ${phaseId}`);
        return;
    }

    console.warn(`[FeatureFlags] Rolling back Phase ${phaseId}...`);
    weeks.forEach(week => {
      week.flags.forEach(flag => {
        this.disable(flag as keyof GlobalFeatureFlags);
      });
    });

    toast.warning(`Rolled back Phase ${phaseId}`, {
      description: `Disabled ${weeks.reduce((sum, w) => sum + w.flags.length, 0)} flags`
    });
  }
}

export const globalFeatureFlags = new GlobalFeatureFlagManager();


// Compatibility exports
export const optimizationFlags = globalFeatureFlags;

// Expose to window for console access
if (typeof window !== 'undefined') {
  (window as any).globalFeatureFlags = globalFeatureFlags;
  (window as any).optimizationFlags = globalFeatureFlags;
}

export { useGlobalFeatureFlag } from '@/contexts/FeatureFlagContext';
